import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import GoogleMapComponent, { type AlarmMarker } from '../components/GoogleMap';
import RealTimeLearningDashboard from '../components/training/RealTimeLearningDashboard';
import InternetLearningMonitor from '../components/training/InternetLearningMonitor';
import { 
  Layout, 
  Typography, 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Table,
  Tag,
  Space,
  Button,
  Alert,
  Progress,
  Badge,
  Timeline,
  Avatar,
  Tooltip,
  Switch,
  Select,
  Input,
  Modal,
  message,
  Drawer,
  Descriptions,
  Divider,
  Slider
} from 'antd';
import { 
  AlertOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  FireOutlined,
  MedicineBoxOutlined,
  CarOutlined,
  SafetyOutlined,
  SoundOutlined,
  DashboardOutlined,
  TeamOutlined,
  SettingOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  UserOutlined,
  HeartOutlined,
  ThunderboltOutlined,
  EyeOutlined,
  BellOutlined,
  MessageOutlined,
  ToolOutlined,
  BarChartOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  StarOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// GetMyHive Emergency Alert Interface
interface EmergencyAlert {
  id: string;
  source: 'beenetwork' | 'radar' | 'ai_system';
  type: 'home_security' | 'panic_button' | 'identity_fraud' | 'device_offline' | 'ai_threat' | 'behavioral_anomaly' | 'network_intrusion' | 'predictive_alert';
  severity: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  location: {
    address: string;
    coordinates: [number, number];
    district: string;
    building?: string;
    apartment?: string;
  };
  user: {
    id: string;
    name: string;
    phone: string;
    membershipLevel: 'basic' | 'premium' | 'enterprise';
  };
  status: 'active' | 'investigating' | 'responded' | 'resolved' | 'false_alarm';
  timestamp: string;
  assignedOperator?: string;
  estimatedResponse?: number; // minutes
  priority: number;
  beenetworkData?: {
    deviceId?: string;
    sensorType?: string;
    signalStrength?: number;
    batteryLevel?: number;
  };
  radarData?: {
    threatLevel?: number;
    confidence?: number;
    aiModel?: string;
    patternId?: string;
  };
}

// Emergency Team Interface
interface EmergencyTeam {
  id: string;
  name: string;
  type: 'fire' | 'medical' | 'police' | 'technical';
  status: 'available' | 'busy' | 'offline';
  location: string;
  members: number;
  contact: string;
  eta?: number;
}

const EmergencyPage: React.FC = () => {
  // Navigation & Routing
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  
  // Determine page mode based on URL
  const getPageMode = () => {
    if (currentPath.includes('/beenetwork')) return 'beenetwork';
    if (currentPath.includes('/emergency/radar-ai')) return 'radar-ai';
    if (currentPath.includes('/support')) return 'support';
    if (currentPath.includes('/technical')) return 'technical';
    if (currentPath.includes('/map')) return 'map';
    return 'main'; // Default emergency dashboard
  };
  
  const pageMode = getPageMode();
  
  // DEBUG: Console log for troubleshooting
  console.log('🔍 DEBUG - Current Path:', currentPath);
  console.log('🔍 DEBUG - Page Mode:', pageMode);
  console.log('🔍 DEBUG - Should show RADAR AI tabs:', pageMode === 'radar-ai');

  // State Management
  const [alerts, setAlerts] = useState<EmergencyAlert[]>([]);
  const [teams, setTeams] = useState<EmergencyTeam[]>([]);
  const [selectedAlert, setSelectedAlert] = useState<EmergencyAlert | null>(null);
  const [alertDrawerVisible, setAlertDrawerVisible] = useState(false);
  const [newAlertModalVisible, setNewAlertModalVisible] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [filterSeverity, setFilterSeverity] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  
  // Messaging System State
  const [selectedMessage, setSelectedMessage] = useState<any>(null);
  const [chatDrawerVisible, setChatDrawerVisible] = useState(false);
  const [newMessageText, setNewMessageText] = useState('');
  const [activeTab, setActiveTab] = useState<'devices' | 'messages'>('devices');

  // Emergency Statistics
  const [stats, setStats] = useState({
    activeAlerts: 12,
    criticalAlerts: 3,
    avgResponseTime: 4.2,
    resolvedToday: 28,
    teamsAvailable: 15,
    teamsDeployed: 8
  });

  // GetMyHive Mock Data - Replace with real API calls
  useEffect(() => {
    const mockAlerts: EmergencyAlert[] = [
      {
        id: 'BEE-001',
        source: 'beenetwork',
        type: 'panic_button',
        severity: 'critical',
        title: '🚨 Panic Button Aktivasyonu',
        description: 'Kullanıcı mobil app üzerinden acil durum butonu. Hemen müdahale gerekli.',
        location: {
          address: 'Bahçelievler Mahallesi, Güvenlik Sitesi A Blok',
          coordinates: [41.0082, 28.9784],
          district: 'Bahçelievler',
          building: 'A Blok',
          apartment: 'Daire 12'
        },
        user: {
          id: 'USER-1755150636424-u8nfzwqp1',
          name: 'RASİM ÇETİN',
          phone: '+905551234567',
          membershipLevel: 'premium'
        },
        status: 'active',
        timestamp: new Date(Date.now() - 3 * 60000).toISOString(),
        assignedOperator: 'OP-001',
        estimatedResponse: 2,
        priority: 1,
        beenetworkData: {
          deviceId: 'BEE-DEV-001',
          sensorType: 'panic_button',
          signalStrength: 85,
          batteryLevel: 78
        }
      },
      {
        id: 'RAD-001',
        source: 'radar',
        type: 'ai_threat',
        severity: 'high',
        title: '🤖 AI Tehdit Tespiti',
        description: 'RADAR sistemi anormal davranış tespit etti. Kimlik doğrulama anomalisi.',
        location: {
          address: 'Atatürk Caddesi, Metropol Residence',
          coordinates: [41.0125, 28.9756],
          district: 'Şişli',
          building: 'B Blok',
          apartment: 'Daire 45'
        },
        user: {
          id: 'USER-1755150636425-x9mfzwqp2',
          name: 'AYŞE KAYA',
          phone: '+905559876543',
          membershipLevel: 'enterprise'
        },
        status: 'investigating',
        timestamp: new Date(Date.now() - 8 * 60000).toISOString(),
        assignedOperator: 'OP-002',
        priority: 2,
        radarData: {
          threatLevel: 78,
          confidence: 92,
          aiModel: 'BehaviorAnalysis-v2.1',
          patternId: 'ANOM-2025-001'
        }
      },
      {
        id: 'BEE-002',
        source: 'beenetwork',
        type: 'home_security',
        severity: 'medium',
        title: '🏠 Ev Güvenlik İhlali',
        description: 'Giriş kapısı sensörü beklenmedik aktivite. Hareket algılayıcısı tetiklendi.',
        location: {
          address: 'Levent Mahallesi, Villa Sitesi',
          coordinates: [41.0815, 29.0122],
          district: 'Beşiktaş',
          building: 'Villa 15'
        },
        user: {
          id: 'USER-1755150636426-y7nfzwqp3',
          name: 'MERVE ÖZTÜRK',
          phone: '+905552223344',
          membershipLevel: 'basic'
        },
        status: 'responded',
        timestamp: new Date(Date.now() - 15 * 60000).toISOString(),
        assignedOperator: 'OP-003',
        priority: 3,
        beenetworkData: {
          deviceId: 'BEE-DOOR-015',
          sensorType: 'door_sensor',
          signalStrength: 92,
          batteryLevel: 65
        }
      },
      {
        id: 'RAD-002',
        source: 'ai_system',
        type: 'predictive_alert',
        severity: 'medium',
        title: '📊 Öngörülü Güvenlik Uyarısı',
        description: 'AI sistemi potansiyel güvenlik riski tespit etti. Önleyici tedbirler öneriliyor.',
        location: {
          address: 'Kadıköy Mahallesi, Smart Towers',
          coordinates: [40.9833, 29.0167],
          district: 'Kadıköy',
          building: 'C Blok',
          apartment: 'Daire 28'
        },
        user: {
          id: 'USER-1755150636427-z8ofzwqp4',
          name: 'ZEHRA YILMAZ',
          phone: '+905553334455',
          membershipLevel: 'premium'
        },
        status: 'active',
        timestamp: new Date(Date.now() - 20 * 60000).toISOString(),
        priority: 4,
        radarData: {
          threatLevel: 45,
          confidence: 76,
          aiModel: 'PredictiveAnalysis-v1.8',
          patternId: 'PRED-2025-002'
        }
      },
      {
        id: 'BEE-003',
        source: 'beenetwork',
        type: 'device_offline',
        severity: 'low',
        title: '📱 Cihaz Bağlantı Kesintisi',
        description: 'BeeNetwork cihazı 10 dakikadır çevrimdışı. Teknik kontrol gerekli.',
        location: {
          address: 'Üsküdar Mahallesi, Güvenlik Evleri',
          coordinates: [41.0214, 29.0047],
          district: 'Üsküdar',
          building: 'D Blok',
          apartment: 'Daire 8'
        },
        user: {
          id: 'USER-1755150636428-w6pfzwqp5',
          name: 'AYLIN DEMIR',
          phone: '+905554445566',
          membershipLevel: 'basic'
        },
        status: 'resolved',
        timestamp: new Date(Date.now() - 25 * 60000).toISOString(),
        assignedOperator: 'OP-004',
        priority: 5,
        beenetworkData: {
          deviceId: 'BEE-MULTI-008',
          sensorType: 'multi_sensor',
          signalStrength: 0,
          batteryLevel: 23
        }
      }
    ];

    const mockTeams: EmergencyTeam[] = [
      {
        id: 'TEAM-001',
        name: 'BeeNetwork Operatör-01',
        type: 'technical',
        status: 'busy',
        location: 'Bahçelievler Bölgesi',
        members: 2,
        contact: '+905551111111',
        eta: 2
      },
      {
        id: 'TEAM-002',
        name: 'RADAR AI Uzmanı-01',
        type: 'technical',
        status: 'available',
        location: 'Merkez Operasyon',
        members: 1,
        contact: '+905552222222'
      },
      {
        id: 'TEAM-003',
        name: 'Güvenlik Müdahale-01',
        type: 'police',
        status: 'busy',
        location: 'Şişli Bölgesi',
        members: 3,
        contact: '+905553333333',
        eta: 5
      },
      {
        id: 'TEAM-004',
        name: 'BeeNetwork Teknik-02',
        type: 'technical',
        status: 'available',
        location: 'Beşiktaş Bölgesi',
        members: 2,
        contact: '+905554444444'
      }
    ];

    setAlerts(mockAlerts);
    setTeams(mockTeams);

    // Auto refresh simulation
    const interval = setInterval(() => {
      if (autoRefresh) {
        // Simulate real-time updates
        setStats(prev => ({
          ...prev,
          activeAlerts: Math.floor(Math.random() * 20) + 5,
          avgResponseTime: Math.round((Math.random() * 3 + 3) * 10) / 10
        }));
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  // GetMyHive Helper Functions
  const getAlertIcon = (type: string, source?: string) => {
    const getSourceColor = (src?: string) => {
      switch (src) {
        case 'beenetwork': return '#52c41a'; // Green for BeeNetwork
        case 'radar': return '#1890ff'; // Blue for RADAR
        case 'ai_system': return '#722ed1'; // Purple for AI
        default: return '#faad14';
      }
    };

    const color = getSourceColor(source);
    
    switch (type) {
      case 'panic_button': return <AlertOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />;
      case 'home_security': return <SafetyOutlined style={{ color, fontSize: '16px' }} />;
      case 'identity_fraud': return <UserOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />;
      case 'device_offline': return <SettingOutlined style={{ color: '#faad14', fontSize: '16px' }} />;
      case 'ai_threat': return <ThunderboltOutlined style={{ color: '#722ed1', fontSize: '16px' }} />;
      case 'behavioral_anomaly': return <EyeOutlined style={{ color: '#1890ff', fontSize: '16px' }} />;
      case 'network_intrusion': return <DashboardOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />;
      case 'predictive_alert': return <BellOutlined style={{ color: '#faad14', fontSize: '16px' }} />;
      default: return <AlertOutlined style={{ color: '#d9d9d9', fontSize: '16px' }} />;
    }
  };

  const getSourceBadge = (source: string) => {
    switch (source) {
      case 'beenetwork': return <Tag color="green">🐝 BeeNetwork</Tag>;
      case 'radar': return <Tag color="blue">📡 RADAR</Tag>;
      case 'ai_system': return <Tag color="purple">🤖 AI System</Tag>;
      default: return <Tag>❓ Unknown</Tag>;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return '#ff4d4f';
      case 'high': return '#fa8c16';
      case 'medium': return '#faad14';
      case 'low': return '#52c41a';
      default: return '#d9d9d9';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'red';
      case 'assigned': return 'orange';
      case 'en_route': return 'blue';
      case 'on_scene': return 'purple';
      case 'resolved': return 'green';
      case 'false_alarm': return 'gray';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Aktif';
      case 'assigned': return 'Atandı';
      case 'en_route': return 'Yolda';
      case 'on_scene': return 'Olay Yerinde';
      case 'resolved': return 'Çözüldü';
      case 'false_alarm': return 'Yanlış Alarm';
      default: return status;
    }
  };

  // Alert Table Columns
  const alertColumns: ColumnsType<EmergencyAlert> = [
    {
      title: 'Öncelik',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      render: (priority: number) => (
        <Badge 
          count={priority} 
          style={{ 
            backgroundColor: priority <= 2 ? '#ff4d4f' : priority <= 4 ? '#fa8c16' : '#52c41a' 
          }} 
        />
      ),
      sorter: (a, b) => a.priority - b.priority,
    },
    {
      title: 'Kaynak/Tür',
      dataIndex: 'source',
      key: 'source',
      width: 140,
      render: (source: string, record: EmergencyAlert) => (
        <Space direction="vertical" size={0}>
          {getSourceBadge(source)}
          <Tooltip title={record.type}>
            <Space>
              {getAlertIcon(record.type, source)}
              <Text style={{ fontSize: '12px' }}>
                {record.type.replace('_', ' ')}
              </Text>
            </Space>
          </Tooltip>
        </Space>
      ),
      filters: [
        { text: '🐝 BeeNetwork', value: 'beenetwork' },
        { text: '📡 RADAR', value: 'radar' },
        { text: '🤖 AI System', value: 'ai_system' },
      ],
      onFilter: (value, record) => record.source === value,
    },
    {
      title: 'Acil Durum & Kullanıcı',
      dataIndex: 'title',
      key: 'title',
      render: (title: string, record: EmergencyAlert) => (
        <div>
          <Text strong style={{ color: getSeverityColor(record.severity) }}>
            {title}
          </Text>
          <br />
          <Space>
            <Avatar size="small" icon={<UserOutlined />} />
            <div>
              <Text style={{ fontSize: '13px', fontWeight: 500 }}>
                {record.user.name}
              </Text>
              <br />
              <Tag 
                size="small" 
                color={
                  record.user.membershipLevel === 'enterprise' ? 'gold' :
                  record.user.membershipLevel === 'premium' ? 'blue' : 'default'
                }
              >
                {record.user.membershipLevel.toUpperCase()}
              </Tag>
            </div>
          </Space>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            📍 {record.location.district} - {record.location.building} {record.location.apartment}
          </Text>
        </div>
      ),
    },
    {
      title: 'Önem',
      dataIndex: 'severity',
      key: 'severity',
      width: 100,
      render: (severity: string) => (
        <Tag color={getSeverityColor(severity)}>
          {severity.toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: 'Kritik', value: 'critical' },
        { text: 'Yüksek', value: 'high' },
        { text: 'Orta', value: 'medium' },
        { text: 'Düşük', value: 'low' },
      ],
      onFilter: (value, record) => record.severity === value,
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Operatör',
      dataIndex: 'assignedOperator',
      key: 'assignedOperator',
      width: 120,
      render: (operator: string) => 
        operator ? <Tag color="blue">{operator}</Tag> : <Tag color="default">Atanmadı</Tag>,
    },
    {
      title: 'Süre',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 100,
      render: (timestamp: string) => {
        const minutes = Math.floor((Date.now() - new Date(timestamp).getTime()) / 60000);
        return (
          <Text type={minutes > 10 ? 'danger' : 'secondary'}>
            {minutes}dk önce
          </Text>
        );
      },
      sorter: (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 150,
      render: (_, record: EmergencyAlert) => (
        <Space>
          <Button 
            type="primary" 
            size="small" 
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedAlert(record);
              setAlertDrawerVisible(true);
            }}
          >
            Detay
          </Button>
          <Button 
            size="small" 
            icon={<PhoneOutlined />}
            onClick={() => message.info(`Aranıyor: ${record.user.phone}`)}
          >
            Kullanıcıyı Ara
          </Button>
        </Space>
      ),
    },
  ];

  // Render different content based on page mode
  const renderPageContent = () => {
    switch (pageMode) {
      case 'beenetwork':
        return renderBeeNetworkPage();
      case 'radar-ai':
        // DISABLE TEST: Return simple white page
        return (
          <div style={{ padding: '50px', textAlign: 'center', backgroundColor: 'white' }}>
            <h1 style={{ color: 'red', fontSize: '48px' }}>🔧 RADAR AI DISABLED TEST</h1>
            <p style={{ fontSize: '24px' }}>pageMode: {pageMode}</p>
            <p style={{ fontSize: '24px' }}>currentPath: {currentPath}</p>
            <p style={{ fontSize: '18px' }}>Bu beyaz sayfa görünüyorsa, component render çalışıyor!</p>
          </div>
        );
        // return renderRadarAIPage();
      case 'support':
        return renderSupportPage();
      case 'technical':
        return renderTechnicalPage();
      case 'map':
        return renderMapPage();
      default:
        return renderMainDashboard();
    }
  };

  // Main Emergency Dashboard (default)
  const renderMainDashboard = () => (
    <>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2} style={{ margin: 0, color: '#ff4d4f' }}>
              🚨 GetMyHive BeeNetwork Acil Durum Merkezi
            </Title>
            <Text type="secondary">
              🐝 BeeNetwork ve 📡 RADAR sistemi gerçek zamanlı güvenlik yönetimi
            </Text>
          </Col>
          <Col>
            <Space>
              <Tooltip title="Ses bildirimleri">
                <Switch 
                  checked={soundEnabled}
                  onChange={setSoundEnabled}
                  checkedChildren={<SoundOutlined />}
                  unCheckedChildren={<SoundOutlined />}
                />
              </Tooltip>
              <Tooltip title="Otomatik yenileme">
                <Switch 
                  checked={autoRefresh}
                  onChange={setAutoRefresh}
                  checkedChildren={<ReloadOutlined spin />}
                  unCheckedChildren={<ReloadOutlined />}
                />
              </Tooltip>
              <Button 
                type="primary" 
                danger
                icon={<AlertOutlined />}
                onClick={() => setNewAlertModalVisible(true)}
              >
                Yeni Acil Durum
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Critical Alerts Banner */}
      {stats.criticalAlerts > 0 && (
        <Alert
          message={`⚠️ ${stats.criticalAlerts} Kritik Acil Durum Aktif!`}
          description="Hemen müdahale gerektiren acil durumlar mevcut. Lütfen öncelikli olarak inceleyin."
          type="error"
          showIcon
          banner
          style={{ marginBottom: '24px' }}
          action={
            <Button size="small" danger type="primary">
              Tümünü Gör
            </Button>
          }
        />
      )}

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic
              title="Aktif Acil Durum"
              value={stats.activeAlerts}
              prefix={<AlertOutlined style={{ color: '#ff4d4f' }} />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic
              title="Kritik Durumlar"
              value={stats.criticalAlerts}
              prefix={<FireOutlined style={{ color: '#ff4d4f' }} />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic
              title="Ort. Müdahale"
              value={stats.avgResponseTime}
              suffix="dk"
              prefix={<ClockCircleOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic
              title="Bugün Çözülen"
              value={stats.resolvedToday}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic
              title="Müsait Ekip"
              value={stats.teamsAvailable}
              prefix={<TeamOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card>
            <Statistic
              title="Görevdeki Ekip"
              value={stats.teamsDeployed}
              prefix={<CarOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Row gutter={[24, 24]}>
        {/* Left Column - Active Alerts */}
        <Col xs={24} xl={16}>
          <Card 
            title={
              <Space>
                <AlertOutlined style={{ color: '#ff4d4f' }} />
                <span>Aktif Acil Durumlar</span>
                <Badge count={alerts.length} style={{ backgroundColor: '#ff4d4f' }} />
              </Space>
            }
            extra={
              <Space>
                <Select
                  placeholder="Önem Seviyesi"
                  style={{ width: 120 }}
                  value={filterSeverity}
                  onChange={setFilterSeverity}
                  size="small"
                >
                  <Option value="all">Tümü</Option>
                  <Option value="critical">Kritik</Option>
                  <Option value="high">Yüksek</Option>
                  <Option value="medium">Orta</Option>
                  <Option value="low">Düşük</Option>
                </Select>
                <Select
                  placeholder="Durum"
                  style={{ width: 120 }}
                  value={filterStatus}
                  onChange={setFilterStatus}
                  size="small"
                >
                  <Option value="all">Tümü</Option>
                  <Option value="active">Aktif</Option>
                  <Option value="assigned">Atandı</Option>
                  <Option value="en_route">Yolda</Option>
                  <Option value="on_scene">Olay Yerinde</Option>
                </Select>
              </Space>
            }
          >
            <Table
              columns={alertColumns}
              dataSource={alerts}
              rowKey="id"
              size="small"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} / ${total} acil durum`,
              }}
              rowClassName={(record) => 
                record.severity === 'critical' ? 'critical-row' : ''
              }
              scroll={{ x: 1200 }}
            />
          </Card>
        </Col>

        {/* Right Column - Teams & Quick Actions */}
        <Col xs={24} xl={8}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Emergency Teams */}
            <Card 
              title={
                <Space>
                  <TeamOutlined style={{ color: '#1890ff' }} />
                  <span>Acil Durum Ekipleri</span>
                </Space>
              }
              size="small"
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                {teams.map(team => (
                  <Card key={team.id} size="small" style={{ width: '100%' }}>
                    <Row justify="space-between" align="middle">
                      <Col>
                        <Space direction="vertical" size={0}>
                          <Text strong>{team.name}</Text>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {team.location} • {team.members} kişi
                          </Text>
                        </Space>
                      </Col>
                      <Col>
                        <Space direction="vertical" size={0} align="end">
                          <Tag color={team.status === 'available' ? 'green' : 'orange'}>
                            {team.status === 'available' ? 'Müsait' : 
                             team.status === 'busy' ? 'Meşgul' : 'Çevrimdışı'}
                          </Tag>
                          {team.eta && (
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {team.eta}dk
                            </Text>
                          )}
                        </Space>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </Space>
            </Card>

            {/* Quick Actions */}
            <Card 
              title={
                <Space>
                  <ThunderboltOutlined style={{ color: '#faad14' }} />
                  <span>Hızlı İşlemler</span>
                </Space>
              }
              size="small"
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button 
                  type="primary" 
                  danger 
                  block 
                  icon={<AlertOutlined />}
                  size="large"
                >
                  🚨 BeeNetwork Genel Alarm
                </Button>
                <Button 
                  type="primary" 
                  block 
                  icon={<DashboardOutlined />}
                  style={{ backgroundColor: '#52c41a' }}
                  onClick={() => navigate('/emergency/beenetwork')}
                >
                  🐝 BeeNetwork Merkezi
                </Button>
                <Button 
                  block 
                  icon={<ThunderboltOutlined />}
                  style={{ backgroundColor: '#722ed1', color: 'white' }}
                  onClick={() => navigate('/emergency/radar-ai')}
                >
                  🤖 RADAR AI Sistemi
                </Button>
                <Button 
                  block 
                  icon={<PhoneOutlined />}
                  style={{ backgroundColor: '#1890ff', color: 'white' }}
                  onClick={() => navigate('/emergency/support')}
                >
                  📞 Kullanıcı Destek Hattı
                </Button>
                <Button 
                  block 
                  icon={<SettingOutlined />}
                  style={{ backgroundColor: '#faad14', color: 'white' }}
                  onClick={() => navigate('/emergency/technical')}
                >
                  🔧 Teknik Destek
                </Button>
                <Button 
                  block 
                  icon={<FireOutlined />}
                  style={{ backgroundColor: '#ff4d4f', color: 'white' }}
                >
                  🆘 Acil Servisler (112)
                </Button>
                <Button 
                  block 
                  icon={<EnvironmentOutlined />}
                  style={{ backgroundColor: '#13c2c2', color: 'white' }}
                  onClick={() => navigate('/emergency/map')}
                >
                  🗺️ Emergency Haritası
                </Button>
              </Space>
            </Card>

            {/* Live Status */}
            <Card 
              title={
                <Space>
                  <HeartOutlined style={{ color: '#ff4d4f' }} />
                  <span>Sistem Durumu</span>
                </Space>
              }
              size="small"
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text>🐝 BeeNetwork Cihazları</Text>
                  <Progress percent={94} size="small" status="active" />
                </div>
                <div>
                  <Text>📡 RADAR AI Sistemi</Text>
                  <Progress percent={98} size="small" status="success" />
                </div>
                <div>
                  <Text>🔐 Kimlik Doğrulama</Text>
                  <Progress percent={89} size="small" status="active" />
                </div>
                <div>
                  <Text>📱 Mobil App Bağlantıları</Text>
                  <Progress percent={96} size="small" status="success" />
                </div>
                <div>
                  <Text>🚨 Alarm Sistemleri</Text>
                  <Progress percent={100} size="small" status="success" />
                </div>
              </Space>
            </Card>
          </Space>
        </Col>
      </Row>
    </>
  );

  // BeeNetwork Devices and Messages Mock Data
  const [beeNetworkDevices] = useState([
    {
      id: 'BEE-DEV-001',
      name: 'Bahçelievler A12 Kapı Sensörü',
      type: 'door_sensor',
      location: 'Bahçelievler Mah. A Blok Daire 12',
      status: 'online',
      battery: 78,
      signalStrength: 85,
      lastSeen: new Date(Date.now() - 2 * 60000).toISOString(),
      user: 'RASİM ÇETİN',
      firmware: 'v2.1.3'
    },
    {
      id: 'BEE-DEV-002',
      name: 'Şişli B45 Motion Sensörü',
      type: 'motion_sensor',
      location: 'Şişli Mah. Metropol Residence B45',
      status: 'online',
      battery: 92,
      signalStrength: 78,
      lastSeen: new Date(Date.now() - 1 * 60000).toISOString(),
      user: 'AYŞE KAYA',
      firmware: 'v2.1.3'
    },
    {
      id: 'BEE-DEV-003',
      name: 'Levent Villa15 Panic Button',
      type: 'panic_button',
      location: 'Levent Mah. Villa Sitesi Villa 15',
      status: 'offline',
      battery: 23,
      signalStrength: 0,
      lastSeen: new Date(Date.now() - 25 * 60000).toISOString(),
      user: 'MERVE ÖZTÜRK',
      firmware: 'v2.0.8'
    },
    {
      id: 'BEE-DEV-004',
      name: 'Kadıköy C28 Multi Sensor',
      type: 'multi_sensor',
      location: 'Kadıköy Mah. Smart Towers C28',
      status: 'online',
      battery: 65,
      signalStrength: 92,
      lastSeen: new Date(Date.now() - 30000).toISOString(),
      user: 'ZEHRA YILMAZ',
      firmware: 'v2.1.3'
    },
    {
      id: 'BEE-DEV-005',
      name: 'Üsküdar D8 Window Sensor',
      type: 'window_sensor',
      location: 'Üsküdar Mah. Güvenlik Evleri D8',
      status: 'warning',
      battery: 15,
      signalStrength: 65,
      lastSeen: new Date(Date.now() - 5 * 60000).toISOString(),
      user: 'AYLIN DEMIR',
      firmware: 'v2.1.0'
    }
  ]);

  const [beeNetworkMessages] = useState([
    {
      id: 'MSG-001',
      type: 'emergency',
      from: 'RASİM ÇETİN',
      deviceId: 'BEE-DEV-001',
      message: 'Acil durum butonu aktivasyonu - Ev içinde şüpheli ses duydum',
      timestamp: new Date(Date.now() - 3 * 60000).toISOString(),
      status: 'new',
      priority: 'critical',
      location: 'Bahçelievler A12'
    },
    {
      id: 'MSG-002',
      type: 'system',
      from: 'BeeNetwork Sistem',
      deviceId: 'BEE-DEV-003',
      message: 'Cihaz 20 dakikadır çevrimdışı - Batarya seviyesi kritik',
      timestamp: new Date(Date.now() - 8 * 60000).toISOString(),
      status: 'acknowledged',
      priority: 'medium',
      location: 'Levent Villa15'
    },
    {
      id: 'MSG-003',
      type: 'alert',
      from: 'AYŞE KAYA',
      deviceId: 'BEE-DEV-002',
      message: 'Motion sensöründe beklenmedik aktivite tespit edildi',
      timestamp: new Date(Date.now() - 12 * 60000).toISOString(),
      status: 'resolved',
      priority: 'high',
      location: 'Şişli B45'
    },
    {
      id: 'MSG-004',
      type: 'maintenance',
      from: 'Teknik Ekip - Zeynep',
      deviceId: 'BEE-DEV-005',
      message: 'Düşük batarya uyarısı - Bakım gerekli',
      timestamp: new Date(Date.now() - 15 * 60000).toISOString(),
      status: 'pending',
      priority: 'low',
      location: 'Üsküdar D8'
    }
  ]);

  // Device Table Columns
  const deviceColumns: ColumnsType<any> = [
    {
      title: 'Cihaz',
      key: 'device',
      render: (_, record) => (
        <Space>
          {record.type === 'door_sensor' && <SafetyOutlined style={{ color: '#52c41a' }} />}
          {record.type === 'motion_sensor' && <EyeOutlined style={{ color: '#1890ff' }} />}
          {record.type === 'panic_button' && <AlertOutlined style={{ color: '#ff4d4f' }} />}
          {record.type === 'multi_sensor' && <DashboardOutlined style={{ color: '#722ed1' }} />}
          {record.type === 'window_sensor' && <EnvironmentOutlined style={{ color: '#faad14' }} />}
          <div>
            <Text strong>{record.name}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.id} • {record.user}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={
          status === 'online' ? 'green' : 
          status === 'offline' ? 'red' : 'orange'
        }>
          {status === 'online' ? 'Çevrimiçi' : 
           status === 'offline' ? 'Çevrimdışı' : 'Uyarı'}
        </Tag>
      ),
    },
    {
      title: 'Batarya',
      dataIndex: 'battery',
      key: 'battery',
      width: 120,
      render: (battery) => (
        <Progress 
          percent={battery} 
          size="small" 
          status={battery < 20 ? 'exception' : battery < 50 ? 'active' : 'success'}
          format={() => `${battery}%`}
        />
      ),
    },
    {
      title: 'Sinyal',
      dataIndex: 'signalStrength',
      key: 'signalStrength',
      width: 100,
      render: (signal) => (
        <Progress 
          percent={signal} 
          size="small" 
          strokeColor={signal > 70 ? '#52c41a' : signal > 40 ? '#faad14' : '#ff4d4f'}
          format={() => `${signal}%`}
        />
      ),
    },
    {
      title: 'Son Görülme',
      dataIndex: 'lastSeen',
      key: 'lastSeen',
      width: 120,
      render: (lastSeen) => {
        const minutes = Math.floor((Date.now() - new Date(lastSeen).getTime()) / 60000);
        return (
          <Text type={minutes > 10 ? 'danger' : 'secondary'}>
            {minutes < 1 ? 'Şimdi' : `${minutes}dk önce`}
          </Text>
        );
      },
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<SettingOutlined />}>
            Ayarlar
          </Button>
          <Button size="small" icon={<EyeOutlined />}>
            Detay
          </Button>
        </Space>
      ),
    },
  ];

  // Message Table Columns
  const messageColumns: ColumnsType<any> = [
    {
      title: 'Tür',
      dataIndex: 'type',
      key: 'type',
      width: 80,
      render: (type) => {
        const getIcon = () => {
          switch (type) {
            case 'emergency': return <AlertOutlined style={{ color: '#ff4d4f' }} />;
            case 'system': return <SettingOutlined style={{ color: '#1890ff' }} />;
            case 'alert': return <BellOutlined style={{ color: '#faad14' }} />;
            case 'maintenance': return <ToolOutlined style={{ color: '#722ed1' }} />;
            default: return <MessageOutlined />;
          }
        };
        return getIcon();
      },
    },
    {
      title: 'Mesaj',
      key: 'message',
      render: (_, record) => (
        <div>
          <Text strong style={{ 
            color: record.priority === 'critical' ? '#ff4d4f' : 
                   record.priority === 'high' ? '#fa8c16' : '#000'
          }}>
            {record.message}
          </Text>
          <br />
          <Space>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.from} • {record.location}
            </Text>
            <Tag size="small" color={
              record.priority === 'critical' ? 'red' :
              record.priority === 'high' ? 'orange' :
              record.priority === 'medium' ? 'blue' : 'default'
            }>
              {record.priority.toUpperCase()}
            </Tag>
          </Space>
        </div>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={
          status === 'new' ? 'red' :
          status === 'acknowledged' ? 'orange' :
          status === 'pending' ? 'blue' : 'green'
        }>
          {status === 'new' ? 'Yeni' :
           status === 'acknowledged' ? 'Görüldü' :
           status === 'pending' ? 'Beklemede' : 'Çözüldü'}
        </Tag>
      ),
    },
    {
      title: 'Zaman',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 100,
      render: (timestamp) => {
        const minutes = Math.floor((Date.now() - new Date(timestamp).getTime()) / 60000);
        return (
          <Text type="secondary">
            {minutes < 1 ? 'Şimdi' : `${minutes}dk önce`}
          </Text>
        );
      },
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button 
            size="small" 
            type="primary" 
            icon={<PhoneOutlined />}
            onClick={() => message.info(`Aranıyor: ${record.from} için destek hattı`)}
          >
            Ara
          </Button>
          <Button 
            size="small" 
            icon={<MessageOutlined />}
            onClick={() => {
              setSelectedMessage(record);
              setChatDrawerVisible(true);
            }}
          >
            Yanıtla
          </Button>
        </Space>
      ),
    },
  ];

  // BeeNetwork Merkezi Page
  const renderBeeNetworkPage = () => (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Button 
              icon={<AlertOutlined />} 
              onClick={() => navigate('/emergency')}
              style={{ marginBottom: '16px' }}
            >
              ← Ana Emergency Dashboard
            </Button>
            <Title level={2} style={{ margin: 0, color: '#52c41a' }}>
              🐝 BeeNetwork Merkezi
            </Title>
            <Text type="secondary">
              Cihaz yönetimi, mesajlaşma sistemi ve BeeNetwork ağ analizi
            </Text>
          </Col>
          <Col>
            <Space>
              <Button type="primary" icon={<ReloadOutlined />}>
                Yenile
              </Button>
              <Button icon={<SettingOutlined />}>
                Ağ Ayarları
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Statistics Overview */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Toplam Cihaz"
              value={beeNetworkDevices.length}
              prefix={<DashboardOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Çevrimiçi"
              value={beeNetworkDevices.filter(d => d.status === 'online').length}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Uyarı"
              value={beeNetworkDevices.filter(d => d.status === 'warning' || d.battery < 20).length}
              prefix={<ExclamationCircleOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Çevrimdışı"
              value={beeNetworkDevices.filter(d => d.status === 'offline').length}
              prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content - Active Tabs */}
      <Card>
        <div style={{ marginBottom: '16px' }}>
          <Space>
            <Button 
              type={activeTab === 'devices' ? 'primary' : 'default'}
              icon={<DashboardOutlined />}
              onClick={() => setActiveTab('devices')}
            >
              📱 Cihaz Yönetimi
            </Button>
            <Badge count={beeNetworkMessages.filter(m => m.status === 'new').length}>
              <Button 
                type={activeTab === 'messages' ? 'primary' : 'default'}
                icon={<MessageOutlined />}
                onClick={() => setActiveTab('messages')}
              >
                💬 Mesajlaşma Sistemi
              </Button>
            </Badge>
            <Button 
              type={activeTab === 'ai_analysis' ? 'primary' : 'default'}
              icon={<BarChartOutlined />}
              onClick={() => setActiveTab('ai_analysis')}
            >
              🤖 AI Yakınlık Analizi
            </Button>
          </Space>
        </div>

        {/* Conditional Content Based on Active Tab */}
        {activeTab === 'devices' && (
          <div>
            <Title level={4} style={{ color: '#52c41a' }}>
              📱 BeeNetwork Cihaz Yönetimi
            </Title>
            <Table
              columns={deviceColumns}
              dataSource={beeNetworkDevices}
              rowKey="id"
              size="small"
              pagination={{
                pageSize: 8,
                showSizeChanger: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} / ${total} cihaz`,
              }}
              rowClassName={(record) => 
                record.status === 'offline' ? 'critical-row' : 
                record.battery < 20 ? 'warning-row' : ''
              }
            />
          </div>
        )}

        {activeTab === 'messages' && (
          <div>
            <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
              <Col>
                <Title level={4} style={{ color: '#1890ff' }}>
                  💬 BeeNetwork Mesajlaşma Sistemi
                </Title>
              </Col>
              <Col>
                <Button 
                  type="primary" 
                  icon={<MessageOutlined />}
                  onClick={() => message.info('Yeni mesaj sistemi yakında...')}
                >
                  Yeni Mesaj Gönder
                </Button>
              </Col>
            </Row>
            <Table
              columns={messageColumns}
              dataSource={beeNetworkMessages}
              rowKey="id"
              size="small"
              pagination={{
                pageSize: 8,
                showSizeChanger: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} / ${total} mesaj`,
              }}
              rowClassName={(record) => 
                record.priority === 'critical' ? 'critical-row' : 
                record.status === 'new' ? 'new-message-row' : ''
              }
            />
          </div>
        )}

        {activeTab === 'ai_analysis' && (
          <div>
            <Title level={4} style={{ color: '#722ed1' }}>
              🤖 AI Yakınlık Analizi ve BeeNetwork Üye Konumları
            </Title>
            
            {/* AI Statistics */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="BeeNetwork Üyeleri"
                    value={beeNetworkMembers.length}
                    prefix={<TeamOutlined style={{ color: '#52c41a' }} />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="Çevrimiçi Üyeler"
                    value={beeNetworkMembers.filter(m => m.status === 'online').length}
                    prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="Ortalama Rating"
                    value={4.8}
                    precision={1}
                    prefix={<StarOutlined style={{ color: '#faad14' }} />}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="AI Kapsama Alanı"
                    value="98.5"
                    suffix="%"
                    prefix={<EnvironmentOutlined style={{ color: '#13c2c2' }} />}
                    valueStyle={{ color: '#13c2c2' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* BeeNetwork Members Table */}
            <Table
              columns={[
                {
                  title: 'Üye',
                  key: 'member',
                  render: (_, record) => (
                    <Space>
                      <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#52c41a' }} />
                      <div>
                        <Text strong>{record.name}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {record.location}
                        </Text>
                      </div>
                    </Space>
                  ),
                },
                {
                  title: 'Durum',
                  dataIndex: 'status',
                  key: 'status',
                  width: 100,
                  render: (status) => (
                    <Tag color={status === 'online' ? 'green' : 'orange'}>
                      {status === 'online' ? 'Çevrimiçi' : 'Meşgul'}
                    </Tag>
                  ),
                },
                {
                  title: 'Uzmanlık',
                  dataIndex: 'expertise',
                  key: 'expertise',
                  width: 200,
                  render: (expertise) => (
                    <Space wrap>
                      {expertise.map((skill: string, index: number) => (
                        <Tag key={index} color="blue" size="small">
                          {skill}
                        </Tag>
                      ))}
                    </Space>
                  ),
                },
                {
                  title: 'Yanıt Süresi',
                  dataIndex: 'responseTime',
                  key: 'responseTime',
                  width: 100,
                  render: (time) => (
                    <Text style={{ color: '#1890ff' }}>{time}</Text>
                  ),
                },
                {
                  title: 'Rating',
                  dataIndex: 'rating',
                  key: 'rating',
                  width: 100,
                  render: (rating) => (
                    <Space>
                      <StarOutlined style={{ color: '#faad14' }} />
                      <Text>{rating}</Text>
                    </Space>
                  ),
                },
                {
                  title: 'Koordinatlar',
                  key: 'coordinates',
                  width: 150,
                  render: (_, record) => (
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                      {record.coordinates.lat.toFixed(4)}, {record.coordinates.lng.toFixed(4)}
                    </Text>
                  ),
                },
                {
                  title: 'AI Test',
                  key: 'actions',
                  width: 120,
                  render: (_, record) => (
                    <Button 
                      size="small" 
                      icon={<EnvironmentOutlined />}
                      onClick={() => {
                        // Simulate proximity test
                        const testAlarmLat = 41.0082 + (Math.random() - 0.5) * 0.1;
                        const testAlarmLng = 28.9784 + (Math.random() - 0.5) * 0.1;
                        const proximityData = calculateProximityAnalysis(testAlarmLat, testAlarmLng);
                        
                        const memberResult = proximityData.find(m => m.id === record.id);
                        if (memberResult) {
                          message.success(
                            `🤖 ${record.name}: ${memberResult.distance.toFixed(1)}km uzaklıkta - ${memberResult.estimatedArrival} varış süresi`
                          );
                        } else {
                          message.warning(`🤖 ${record.name}: Test alanı dışında`);
                        }
                      }}
                    >
                      Test Et
                    </Button>
                  ),
                },
              ]}
              dataSource={beeNetworkMembers}
              rowKey="id"
              size="small"
              pagination={{
                pageSize: 8,
                showSizeChanger: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} / ${total} üye`,
              }}
              rowClassName={(record) => 
                record.status === 'online' ? 'online-member-row' : ''
              }
            />

            {/* AI Proximity Demo */}
            <Card 
              title="🎯 AI Yakınlık Simülasyonu" 
              style={{ marginTop: '24px' }}
              extra={
                <Button 
                  type="primary" 
                  icon={<ThunderboltOutlined />}
                  onClick={() => {
                    // Generate random alarm location
                    const testLat = 41.0082 + (Math.random() - 0.5) * 0.1;
                    const testLng = 28.9784 + (Math.random() - 0.5) * 0.1;
                    const proximityData = calculateProximityAnalysis(testLat, testLng);
                    
                    if (proximityData.length > 0) {
                      message.success(
                        `🤖 AI Analizi: ${proximityData.length} müsait üye bulundu. En yakın: ${proximityData[0].name} (${proximityData[0].distance.toFixed(1)}km)`
                      );
                    } else {
                      message.warning('🤖 AI: 5km çevrede müsait üye bulunamadı');
                    }
                  }}
                >
                  Rastgele Alarm Simülasyonu
                </Button>
              }
            >
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Text type="secondary">
                  🧠 AI sistemi gerçek zamanlı olarak alarm konumları ile BeeNetwork üyelerinin konumlarını analiz eder
                </Text>
                <br />
                <Text type="secondary">
                  📍 5km çevredeki müsait üyeleri bulur, uzaklık hesaplar ve tahmini varış süresi verir
                </Text>
                <br />
                <Text type="secondary">
                  ⚡ Harita üzerindeki herhangi bir alarm işaretçisine tıklayarak AI analizini test edebilirsiniz
                </Text>
              </div>
            </Card>
          </div>
        )}
      </Card>

      <style>{`
        .online-member-row {
          background-color: #f6ffed !important;
          border-left: 4px solid #52c41a;
        }
        .online-member-row:hover {
          background-color: #f0f9e8 !important;
        }
      `}</style>

      {/* Chat Drawer for Messaging */}
      <Drawer
        title={
          <Space>
            <MessageOutlined style={{ color: '#1890ff' }} />
            <span>Mesaj Yanıtla</span>
            {selectedMessage && (
              <Tag color={
                selectedMessage.priority === 'critical' ? 'red' :
                selectedMessage.priority === 'high' ? 'orange' :
                selectedMessage.priority === 'medium' ? 'blue' : 'default'
              }>
                {selectedMessage.priority.toUpperCase()}
              </Tag>
            )}
          </Space>
        }
        placement="right"
        width={500}
        onClose={() => setChatDrawerVisible(false)}
        open={chatDrawerVisible}
        extra={
          <Space>
            <Button icon={<PhoneOutlined />}>
              Ara
            </Button>
            <Button type="primary" icon={<CheckCircleOutlined />}>
              Çözüldü İşaretle
            </Button>
          </Space>
        }
      >
        {selectedMessage && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Original Message */}
            <Card size="small" style={{ backgroundColor: '#f6ffed' }}>
              <div style={{ marginBottom: '8px' }}>
                <Space>
                  <Avatar size="small" icon={<UserOutlined />} />
                  <Text strong>{selectedMessage.from}</Text>
                  <Tag size="small">{selectedMessage.type}</Tag>
                </Space>
              </div>
              <Text>{selectedMessage.message}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                📍 {selectedMessage.location} • {new Date(selectedMessage.timestamp).toLocaleString('tr-TR')}
              </Text>
            </Card>

            {/* Chat Messages Area */}
            <div style={{ height: '300px', border: '1px solid #d9d9d9', borderRadius: '6px', padding: '12px', backgroundColor: '#fafafa' }}>
              <Title level={5} style={{ margin: '0 0 16px 0' }}>💬 Konuşma Geçmişi</Title>
              
              {/* Sample Chat Messages */}
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <div style={{ textAlign: 'right' }}>
                  <div style={{ 
                    backgroundColor: '#1890ff', 
                    color: 'white', 
                    padding: '8px 12px', 
                    borderRadius: '12px 12px 4px 12px',
                    display: 'inline-block',
                    maxWidth: '80%'
                  }}>
                    Merhaba, mesajınızı aldık. Durumunuz nasıl?
                  </div>
                  <br />
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    Operatör • 2dk önce
                  </Text>
                </div>
                
                <div style={{ textAlign: 'left' }}>
                  <div style={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #d9d9d9',
                    padding: '8px 12px', 
                    borderRadius: '12px 12px 12px 4px',
                    display: 'inline-block',
                    maxWidth: '80%'
                  }}>
                    Şu anda evdeyim, ses gelmedi. Yanlış alarm olabilir.
                  </div>
                  <br />
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    {selectedMessage.from} • 1dk önce
                  </Text>
                </div>
              </Space>
            </div>

            {/* Message Input */}
            <div>
              <Title level={5}>✍️ Yanıt Gönder</Title>
              <Space.Compact style={{ width: '100%' }}>
                <TextArea
                  value={newMessageText}
                  onChange={(e) => setNewMessageText(e.target.value)}
                  placeholder="Mesajınızı yazın..."
                  rows={3}
                  style={{ width: '100%' }}
                />
                <Button 
                  type="primary" 
                  icon={<MessageOutlined />}
                  style={{ height: 'auto' }}
                  onClick={() => {
                    if (newMessageText.trim()) {
                      message.success('Mesaj gönderildi!');
                      setNewMessageText('');
                      // Here you would send the message to backend
                    }
                  }}
                >
                  Gönder
                </Button>
              </Space.Compact>
            </div>

            {/* Quick Actions */}
            <div>
              <Title level={5}>⚡ Hızlı İşlemler</Title>
              <Space wrap>
                <Button size="small" onClick={() => setNewMessageText('Güvenlik ekibi yollandı, 5 dakika içinde orada olacaklar.')}>
                  🚔 Güvenlik Gönder
                </Button>
                <Button size="small" onClick={() => setNewMessageText('Teknik ekip cihazınızı kontrol edecek.')}>
                  🔧 Teknik Destek
                </Button>
                <Button size="small" onClick={() => setNewMessageText('Durumunuz izleniyor, güvendeyseniz bildirin.')}>
                  👁️ Takip Et
                </Button>
                <Button size="small" onClick={() => setNewMessageText('Yanlış alarm olarak işaretlendi.')}>
                  ❌ Yanlış Alarm
                </Button>
              </Space>
            </div>
          </Space>
        )}
      </Drawer>

      {/* Custom Styles */}
      <style>{`
        .warning-row {
          background-color: #fffbf0 !important;
          border-left: 4px solid #faad14;
        }
        .new-message-row {
          background-color: #f6ffed !important;
          border-left: 4px solid #52c41a;
        }
        .new-message-row:hover {
          background-color: #f0f9e8 !important;
        }
      `}</style>
    </div>
  );

  // AI Threat Patterns Mock Data
  const [aiThreatPatterns] = useState(() => [
    {
      id: 'PATTERN-001',
      name: 'Gece Saatlerinde Tekrarlayan Panik Buton Aktivasyonu',
      type: 'behavioral_anomaly',
      severity: 'high',
      confidence: 94.7,
      occurrences: 12,
      timeRange: '02:00-04:00',
      affectedUsers: ['AYŞE KAYA', 'MERVE ÖZTÜRK'],
      description: 'Aynı kullanıcılar gece saatlerinde tekrarlayan panik buton aktivasyonu gerçekleştiriyor',
      aiModel: 'BehaviorAI-v2.1',
      status: 'active',
      lastDetected: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      risk: 'Potansiyel yanlış alarm patterns veya sistem manipülasyonu'
    },
    {
      id: 'PATTERN-002', 
      name: 'Koordineli Çoklu Sensör Tetiklenmesi',
      type: 'network_intrusion',
      severity: 'critical',
      confidence: 98.2,
      occurrences: 5,
      timeRange: '14:30-15:00',
      affectedUsers: ['ZEHRA YILMAZ', 'AYLIN DEMIR', 'FATMA ÖZKAN'],
      description: 'Aynı anda çoklu sensörlerin koordineli tetiklenmesi - şüpheli aktivite',
      aiModel: 'ThreatAI-v3.0',
      status: 'investigating',
      lastDetected: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      risk: 'Potansiyel sistem saldırısı veya koordineli tehdit'
    },
    {
      id: 'PATTERN-003',
      name: 'Anormal Cihaz Çevrimdışı Patternleri',
      type: 'device_anomaly',
      severity: 'medium',
      confidence: 87.3,
      occurrences: 23,
      timeRange: 'Rastgele',
      affectedUsers: ['AYŞE KAYA'],
      description: 'Cihazların anormal sıklıkta çevrimdışı olması ve tekrar bağlanması',
      aiModel: 'DeviceAI-v1.8',
      status: 'resolved',
      lastDetected: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      risk: 'Cihaz arızası veya güç problemi'
    },
    {
      id: 'PATTERN-004',
      name: 'Kimlik Doğrulama Anomalisi',
      type: 'identity_fraud',
      severity: 'critical',
      confidence: 96.1,
      occurrences: 3,
      timeRange: '09:00-11:00',
      affectedUsers: ['MERVE ÖZTÜRK'],
      description: 'Farklı lokasyonlardan eş zamanlı kimlik doğrulama denemeleri',
      aiModel: 'IdentityAI-v2.5',
      status: 'active',
      lastDetected: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
      risk: 'Potansiyel kimlik hırsızlığı veya hesap ele geçirme'
    }
  ]);

  // AI Analysis State
  const [radarActiveTab, setRadarActiveTab] = useState('threats');
  const [aiAnalysisRunning, setAiAnalysisRunning] = useState(false);

  // AI Learning System Mock Data
  const [aiModels] = useState(() => [
    {
      id: 'behavior-ai-v2.1',
      name: 'BehaviorAI',
      version: 'v2.1',
      type: 'behavioral_analysis',
      status: 'active',
      accuracy: 94.7,
      trainingData: 125420,
      lastTrained: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      learningRate: 0.001,
      epochs: 150,
      falsePositiveRate: 5.3,
      improvements: '+2.1% accuracy this week',
      autoLearning: true
    },
    {
      id: 'threat-ai-v3.0',
      name: 'ThreatAI',
      version: 'v3.0',
      type: 'threat_detection',
      status: 'training',
      accuracy: 98.2,
      trainingData: 89340,
      lastTrained: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
      learningRate: 0.0008,
      epochs: 200,
      falsePositiveRate: 1.8,
      improvements: '+4.2% accuracy this week',
      autoLearning: true
    },
    {
      id: 'device-ai-v1.8',
      name: 'DeviceAI',
      version: 'v1.8',
      type: 'device_monitoring',
      status: 'active',
      accuracy: 87.3,
      trainingData: 67890,
      lastTrained: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      learningRate: 0.002,
      epochs: 100,
      falsePositiveRate: 12.7,
      improvements: '+1.5% accuracy this week',
      autoLearning: false
    },
    {
      id: 'identity-ai-v2.5',
      name: 'IdentityAI',
      version: 'v2.5',
      type: 'identity_verification',
      status: 'active',
      accuracy: 96.1,
      trainingData: 234560,
      lastTrained: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
      learningRate: 0.0005,
      epochs: 180,
      falsePositiveRate: 3.9,
      improvements: '+3.8% accuracy this week',
      autoLearning: true
    }
  ]);

  // AI Learning Analytics
  const [learningAnalytics] = useState(() => ({
    totalLearningEvents: 15840,
    userFeedbacks: 2340,
    autoDiscoveredPatterns: 47,
    modelUpdatesThisWeek: 23,
    avgAccuracyImprovement: 2.9,
    learningEfficiency: 96.4
  }));

  // AI Training State
  const [trainingInProgress, setTrainingInProgress] = useState(false);
  const [selectedModelForTraining, setSelectedModelForTraining] = useState<string | null>(null);

  // RADAR AI System Page - Advanced Threat Detection
  const renderRadarAIPage = () => (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Button 
              icon={<AlertOutlined />} 
              onClick={() => navigate('/emergency')}
              style={{ marginBottom: '16px' }}
            >
              ← Ana Emergency Dashboard
            </Button>
            <Title level={2} style={{ margin: 0, color: '#722ed1' }}>
              🤖 RADAR AI Sistemi
            </Title>
            <Text type="secondary">
              Yapay zeka tehdit tespiti, pattern analizi ve davranış anomalisi
            </Text>
          </Col>
          <Col>
            <Space>
              <Button 
                type="primary" 
                icon={<ThunderboltOutlined />}
                loading={aiAnalysisRunning}
                onClick={() => {
                  setAiAnalysisRunning(true);
                  setTimeout(() => {
                    setAiAnalysisRunning(false);
                    message.success('🤖 AI Analizi tamamlandı - 2 yeni pattern tespit edildi');
                  }, 3000);
                }}
              >
                AI Analizi Başlat
              </Button>
              <Button icon={<SettingOutlined />}>
                Model Ayarları
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* AI Statistics Overview */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="AI Model Versiyonu"
              value="v3.0"
              prefix={<ThunderboltOutlined style={{ color: '#722ed1' }} />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Günlük Analiz"
              value={12458}
              prefix={<EyeOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Tespit Edilen Pattern"
              value={aiThreatPatterns.length}
              prefix={<AlertOutlined style={{ color: '#ff4d4f' }} />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="AI Güven Oranı"
              value={94.7}
              suffix="%"
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content Tabs - ENHANCED VISIBILITY */}
      <Card style={{ 
        marginBottom: '24px', 
        border: '2px solid #722ed1',
        zIndex: 9999,
        position: 'relative'
      }}>
        <div style={{ 
          marginBottom: '16px', 
          padding: '16px',
          backgroundColor: '#f0f2ff',
          borderRadius: '8px',
          zIndex: 10000,
          position: 'relative'
        }}>
          <Title level={4} style={{ marginBottom: '16px', color: '#722ed1' }}>
            📊 RADAR AI Sistemi - Analiz Modülleri
          </Title>
          <Space size="large" wrap>
            <Button 
              type={radarActiveTab === 'threats' ? 'primary' : 'default'}
              icon={<AlertOutlined />}
              onClick={() => setRadarActiveTab('threats')}
              size="large"
              style={{ 
                height: '48px',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              🚨 Tehdit Tespiti
            </Button>
            <Button 
              type={radarActiveTab === 'patterns' ? 'primary' : 'default'}
              icon={<BarChartOutlined />}
              onClick={() => setRadarActiveTab('patterns')}
              size="large"
              style={{ 
                height: '48px',
                fontSize: '14px',
                fontWeight: 'bold',
                backgroundColor: radarActiveTab === 'patterns' ? '#52c41a' : undefined
              }}
            >
              📊 Pattern Analizi
            </Button>
            <Button 
              type={radarActiveTab === 'behavior' ? 'primary' : 'default'}
              icon={<EyeOutlined />}
              onClick={() => setRadarActiveTab('behavior')}
              size="large"
              style={{ 
                height: '48px',
                fontSize: '14px',
                fontWeight: 'bold',
                backgroundColor: radarActiveTab === 'behavior' ? '#1890ff' : undefined
              }}
            >
              🧠 Davranış Anomalisi
            </Button>
            <Button 
              type={radarActiveTab === 'models' ? 'primary' : 'default'}
              icon={<SettingOutlined />}
              onClick={() => setRadarActiveTab('models')}
              size="large"
              style={{ 
                height: '48px',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              ⚙️ AI Model Yönetimi
            </Button>
          </Space>
          
          {/* Active Tab Indicator */}
          <div style={{ marginTop: '16px', padding: '8px', backgroundColor: '#fff', borderRadius: '4px' }}>
            <Text strong style={{ color: '#722ed1' }}>
              Aktif Modül: {
                radarActiveTab === 'threats' ? '🚨 Tehdit Tespiti' :
                radarActiveTab === 'patterns' ? '📊 Pattern Analizi' :
                radarActiveTab === 'behavior' ? '🧠 Davranış Anomalisi' :
                '⚙️ AI Model Yönetimi'
              }
            </Text>
          </div>
        </div>

        {/* Threat Detection Tab */}
        {radarActiveTab === 'threats' && (
          <div>
            <Title level={4} style={{ color: '#ff4d4f' }}>
              🚨 Gerçek Zamanlı Tehdit Tespiti
            </Title>
            
            {/* Active Threats */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
              <Col span={8}>
                <Card size="small" style={{ backgroundColor: '#fff2f0' }}>
                  <Statistic
                    title="Aktif Tehditler"
                    value={aiThreatPatterns.filter(p => p.status === 'active').length}
                    prefix={<FireOutlined style={{ color: '#ff4d4f' }} />}
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" style={{ backgroundColor: '#fff7e6' }}>
                  <Statistic
                    title="İnceleme Altında"
                    value={aiThreatPatterns.filter(p => p.status === 'investigating').length}
                    prefix={<ExclamationCircleOutlined style={{ color: '#faad14' }} />}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" style={{ backgroundColor: '#f6ffed' }}>
                  <Statistic
                    title="Çözümlenen"
                    value={aiThreatPatterns.filter(p => p.status === 'resolved').length}
                    prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Threat Patterns Table */}
            <Table
              columns={[
                {
                  title: 'Pattern',
                  key: 'pattern',
                  render: (_, record) => (
                    <Space direction="vertical" size={0}>
                      <Text strong style={{ color: '#722ed1' }}>{record.name}</Text>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Model: {record.aiModel}
                      </Text>
                    </Space>
                  ),
                },
                {
                  title: 'Tür',
                  dataIndex: 'type',
                  key: 'type',
                  width: 120,
                  render: (type) => {
                    const typeColors = {
                      'behavioral_anomaly': 'purple',
                      'network_intrusion': 'red',
                      'device_anomaly': 'orange',
                      'identity_fraud': 'volcano'
                    };
                    const typeLabels = {
                      'behavioral_anomaly': 'Davranış',
                      'network_intrusion': 'Saldırı',
                      'device_anomaly': 'Cihaz',
                      'identity_fraud': 'Kimlik'
                    };
                    return (
                      <Tag color={typeColors[type as keyof typeof typeColors]}>
                        {typeLabels[type as keyof typeof typeLabels]}
                      </Tag>
                    );
                  },
                },
                {
                  title: 'Önem',
                  dataIndex: 'severity',
                  key: 'severity',
                  width: 100,
                  render: (severity) => (
                    <Tag color={severity === 'critical' ? 'red' : severity === 'high' ? 'orange' : 'blue'}>
                      {severity.toUpperCase()}
                    </Tag>
                  ),
                },
                {
                  title: 'Güven',
                  dataIndex: 'confidence',
                  key: 'confidence',
                  width: 100,
                  render: (confidence) => (
                    <Space>
                      <Progress 
                        percent={confidence} 
                        size="small" 
                        status={confidence > 90 ? 'success' : confidence > 70 ? 'active' : 'exception'}
                        format={() => `${confidence}%`}
                      />
                    </Space>
                  ),
                },
                {
                  title: 'Tekrar',
                  dataIndex: 'occurrences',
                  key: 'occurrences',
                  width: 80,
                  render: (count) => (
                    <Text style={{ color: count > 10 ? '#ff4d4f' : '#1890ff' }}>
                      {count}x
                    </Text>
                  ),
                },
                {
                  title: 'Durum',
                  dataIndex: 'status',
                  key: 'status',
                  width: 120,
                  render: (status) => {
                    const statusColors = {
                      'active': 'red',
                      'investigating': 'orange', 
                      'resolved': 'green'
                    };
                    const statusLabels = {
                      'active': 'Aktif',
                      'investigating': 'İnceleniyor',
                      'resolved': 'Çözüldü'
                    };
                    return (
                      <Tag color={statusColors[status as keyof typeof statusColors]}>
                        {statusLabels[status as keyof typeof statusLabels]}
                      </Tag>
                    );
                  },
                },
                {
                  title: 'İşlemler',
                  key: 'actions',
                  width: 150,
                  render: (_, record) => (
                    <Space>
                      <Button 
                        size="small" 
                        icon={<EyeOutlined />}
                        onClick={() => {
                          Modal.info({
                            title: `🤖 AI Pattern Detayı: ${record.name}`,
                            width: 600,
                            content: (
                              <Space direction="vertical" style={{ width: '100%' }}>
                                <Text><strong>Açıklama:</strong> {record.description}</Text>
                                <Text><strong>Risk:</strong> {record.risk}</Text>
                                <Text><strong>Etkilenen Kullanıcılar:</strong> {record.affectedUsers.join(', ')}</Text>
                                <Text><strong>Zaman Aralığı:</strong> {record.timeRange}</Text>
                                <Text><strong>Son Tespit:</strong> {new Date(record.lastDetected).toLocaleString('tr-TR')}</Text>
                              </Space>
                            ),
                          });
                        }}
                      >
                        Detay
                      </Button>
                      {record.status === 'active' && (
                        <Button 
                          size="small" 
                          type="primary"
                          danger
                          icon={<AlertOutlined />}
                          onClick={() => {
                            message.warning(`🚨 ${record.name} için acil müdahale başlatıldı`);
                          }}
                        >
                          Müdahale
                        </Button>
                      )}
                    </Space>
                  ),
                },
              ]}
              dataSource={aiThreatPatterns}
              rowKey="id"
              size="small"
              pagination={{
                pageSize: 8,
                showSizeChanger: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} / ${total} pattern`,
              }}
              rowClassName={(record) => 
                record.severity === 'critical' ? 'critical-row' : 
                record.status === 'active' ? 'warning-row' : ''
              }
            />
          </div>
        )}

        {/* Pattern Analysis Tab */}
        {radarActiveTab === 'patterns' && (
          <div>
            <Title level={4} style={{ color: '#722ed1', marginBottom: 24 }}>
              📊 AI Pattern Analizi Dashboard
            </Title>
            
            {/* Pattern Analizi İstatistikleri */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={6}>
                <Card>
                  <Statistic
                    title="Tespit Edilen Pattern"
                    value={847}
                    prefix={<BarChartOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={6}>
                <Card>
                  <Statistic
                    title="Aktif Tehdit Paterni"
                    value={12}
                    prefix={<AlertOutlined />}
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={6}>
                <Card>
                  <Statistic
                    title="Prediction Doğruluğu"
                    value={91.4}
                    precision={1}
                    prefix={<CheckCircleOutlined />}
                    suffix="%"
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={6}>
                <Card>
                  <Statistic
                    title="Model Güven Skoru"
                    value={8.7}
                    precision={1}
                    prefix={<SafetyOutlined />}
                    suffix="/10"
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Tehdit Pattern Tablosu */}
            <Card 
              title="🎯 Aktif Tehdit Patternleri"
              extra={
                <Space>
                  <Button icon={<ReloadOutlined />} size="small">
                    Yenile
                  </Button>
                  <Button type="primary" size="small">
                    Yeni Pattern Ekle
                  </Button>
                </Space>
              }
              style={{ marginBottom: 24 }}
            >
              <Table
                size="small"
                dataSource={[
                  {
                    key: '1',
                    pattern: 'Gece Saatleri Yoğun Aktivite',
                    category: 'Zaman Bazlı',
                    riskLevel: 'Yüksek',
                    frequency: '23 kez',
                    lastSeen: '1 saat önce',
                    confidence: 94,
                    action: 'Otomatik Uyarı'
                  },
                  {
                    key: '2',
                    pattern: 'Çoklu Lokasyon Erişimi',
                    category: 'Konum Bazlı',
                    riskLevel: 'Kritik',
                    frequency: '8 kez',
                    lastSeen: '15 dakika önce',
                    confidence: 97,
                    action: 'Acil Müdahale'
                  },
                  {
                    key: '3',
                    pattern: 'Bilinmeyen Cihaz Paterni',
                    category: 'Cihaz Bazlı',
                    riskLevel: 'Orta',
                    frequency: '45 kez',
                    lastSeen: '3 saat önce',
                    confidence: 82,
                    action: 'İzleme'
                  },
                  {
                    key: '4',
                    pattern: 'Anormal Veri Transferi',
                    category: 'Ağ Bazlı',
                    riskLevel: 'Yüksek',
                    frequency: '12 kez',
                    lastSeen: '45 dakika önce',
                    confidence: 89,
                    action: 'Otomatik Blok'
                  }
                ]}
                columns={[
                  {
                    title: 'Pattern Adı',
                    dataIndex: 'pattern',
                    key: 'pattern'
                  },
                  {
                    title: 'Kategori',
                    dataIndex: 'category',
                    key: 'category',
                    width: 120,
                    render: (category: string) => (
                      <Tag color="blue">{category}</Tag>
                    )
                  },
                  {
                    title: 'Risk',
                    dataIndex: 'riskLevel',
                    key: 'riskLevel',
                    width: 80,
                    render: (level: string) => (
                      <Tag color={
                        level === 'Kritik' ? 'red' :
                        level === 'Yüksek' ? 'orange' :
                        level === 'Orta' ? 'gold' : 'green'
                      }>
                        {level}
                      </Tag>
                    )
                  },
                  {
                    title: 'Sıklık',
                    dataIndex: 'frequency',
                    key: 'frequency',
                    width: 80
                  },
                  {
                    title: 'Güven',
                    dataIndex: 'confidence',
                    key: 'confidence',
                    width: 70,
                    render: (confidence: number) => `%${confidence}`
                  },
                  {
                    title: 'Son Görülme',
                    dataIndex: 'lastSeen',
                    key: 'lastSeen',
                    width: 120
                  },
                  {
                    title: 'Aksiyon',
                    dataIndex: 'action',
                    key: 'action',
                    width: 120,
                    render: (action: string) => (
                      <Tag color={
                        action === 'Acil Müdahale' ? 'red' :
                        action === 'Otomatik Uyarı' ? 'orange' :
                        action === 'Otomatik Blok' ? 'purple' : 'blue'
                      }>
                        {action}
                      </Tag>
                    )
                  }
                ]}
                pagination={{ pageSize: 5 }}
              />
            </Card>

            {/* Pattern Analizi Grafikleri */}
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <Card title="📈 Pattern Trend Analizi">
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong>Bu Hafta: </Text>
                      <Progress percent={78} size="small" strokeColor="#52c41a" />
                      <Text type="secondary"> +12% artış</Text>
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong>Bu Ay: </Text>
                      <Progress percent={65} size="small" strokeColor="#1890ff" />
                      <Text type="secondary"> +8% artış</Text>
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong>Kritik Patternler: </Text>
                      <Progress percent={23} size="small" strokeColor="#ff4d4f" />
                      <Text type="secondary"> -5% azalış</Text>
                    </div>
                    <div>
                      <Text strong>Çözülen Patternler: </Text>
                      <Progress percent={89} size="small" strokeColor="#722ed1" />
                      <Text type="secondary"> +15% artış</Text>
                    </div>
                  </div>
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card title="🔍 Pattern Kategorileri">
                  <Timeline>
                    <Timeline.Item color="red">
                      <Text strong>Zaman Bazlı Patternler</Text>
                      <br />
                      <Text type="secondary">34 aktif pattern • %78 doğruluk</Text>
                    </Timeline.Item>
                    <Timeline.Item color="orange">
                      <Text strong>Konum Bazlı Patternler</Text>
                      <br />
                      <Text type="secondary">28 aktif pattern • %85 doğruluk</Text>
                    </Timeline.Item>
                    <Timeline.Item color="blue">
                      <Text strong>Cihaz Bazlı Patternler</Text>
                      <br />
                      <Text type="secondary">19 aktif pattern • %91 doğruluk</Text>
                    </Timeline.Item>
                    <Timeline.Item color="purple">
                      <Text strong>Ağ Bazlı Patternler</Text>
                      <br />
                      <Text type="secondary">15 aktif pattern • %94 doğruluk</Text>
                    </Timeline.Item>
                  </Timeline>
                </Card>
              </Col>
            </Row>
          </div>
        )}

        {/* Behavior Analysis Tab */}
        {radarActiveTab === 'behavior' && (
          <div>
            <Title level={4} style={{ color: '#722ed1', marginBottom: 24 }}>
              🧠 Davranış Anomalisi Tespiti
            </Title>
            
            {/* Davranış Analizi İstatistikleri */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={6}>
                <Card>
                  <Statistic
                    title="Analiz Edilen Kullanıcı"
                    value={1247}
                    prefix={<UserOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={6}>
                <Card>
                  <Statistic
                    title="Tespit Edilen Anomali"
                    value={23}
                    prefix={<ExclamationCircleOutlined />}
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={6}>
                <Card>
                  <Statistic
                    title="Risk Skoru Ortalaması"
                    value={7.3}
                    precision={1}
                    prefix={<SafetyOutlined />}
                    suffix="/10"
                    valueStyle={{ color: '#fa8c16' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={6}>
                <Card>
                  <Statistic
                    title="Doğruluk Oranı"
                    value={94.7}
                    precision={1}
                    prefix={<CheckCircleOutlined />}
                    suffix="%"
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Son Davranış Anomalileri */}
            <Card 
              title="🚨 Son Tespit Edilen Davranış Anomalileri"
              extra={
                <Button icon={<ReloadOutlined />} size="small">
                  Yenile
                </Button>
              }
            >
              <Table
                size="small"
                dataSource={[
                  {
                    key: '1',
                    user: 'Kullanıcı-4521',
                    anomaly: 'Olağandışı giriş saatleri',
                    riskLevel: 'Orta',
                    location: 'Kadıköy, İstanbul',
                    timestamp: '2 dakika önce',
                    confidence: 87
                  },
                  {
                    key: '2', 
                    user: 'Kullanıcı-7832',
                    anomaly: 'Hızlı konum değişimi',
                    riskLevel: 'Yüksek',
                    location: 'Beşiktaş → Sarıyer',
                    timestamp: '8 dakika önce',
                    confidence: 92
                  },
                  {
                    key: '3',
                    user: 'Kullanıcı-2156',
                    anomaly: 'Bilinmeyen cihaz kullanımı',
                    riskLevel: 'Kritik',
                    location: 'Şişli, İstanbul',
                    timestamp: '15 dakika önce',
                    confidence: 96
                  },
                  {
                    key: '4',
                    user: 'Kullanıcı-9043',
                    anomaly: 'Olağandışı uygulama kullanımı',
                    riskLevel: 'Düşük',
                    location: 'Üsküdar, İstanbul',
                    timestamp: '22 dakika önce',
                    confidence: 73
                  }
                ]}
                columns={[
                  {
                    title: 'Kullanıcı',
                    dataIndex: 'user',
                    key: 'user',
                    width: 120
                  },
                  {
                    title: 'Anomali Türü',
                    dataIndex: 'anomaly',
                    key: 'anomaly'
                  },
                  {
                    title: 'Risk Seviyesi',
                    dataIndex: 'riskLevel',
                    key: 'riskLevel',
                    width: 100,
                    render: (level: string) => (
                      <Tag color={
                        level === 'Kritik' ? 'red' :
                        level === 'Yüksek' ? 'orange' :
                        level === 'Orta' ? 'gold' : 'green'
                      }>
                        {level}
                      </Tag>
                    )
                  },
                  {
                    title: 'Konum',
                    dataIndex: 'location',
                    key: 'location',
                    width: 150
                  },
                  {
                    title: 'Güven',
                    dataIndex: 'confidence',
                    key: 'confidence',
                    width: 80,
                    render: (confidence: number) => `%${confidence}`
                  },
                  {
                    title: 'Zaman',
                    dataIndex: 'timestamp',
                    key: 'timestamp',
                    width: 120
                  }
                ]}
                pagination={{ pageSize: 5 }}
              />
            </Card>

            {/* Davranış Analizi Grafikleri */}
            <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
              <Col xs={24} lg={12}>
                <Card title="📊 Anomali Türleri Dağılımı">
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong>Giriş Anomalileri: </Text>
                      <Progress percent={45} size="small" />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong>Konum Anomalileri: </Text>
                      <Progress percent={32} size="small" />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong>Cihaz Anomalileri: </Text>
                      <Progress percent={18} size="small" />
                    </div>
                    <div>
                      <Text strong>Uygulama Anomalileri: </Text>
                      <Progress percent={5} size="small" />
                    </div>
                  </div>
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card title="⏱️ Gerçek Zamanlı İzleme">
                  <Timeline>
                    <Timeline.Item color="red">
                      <Text strong>13:45</Text> - Kritik anomali tespit edildi
                      <br />
                      <Text type="secondary">Kullanıcı-2156: Bilinmeyen cihaz</Text>
                    </Timeline.Item>
                    <Timeline.Item color="orange">
                      <Text strong>13:37</Text> - Yüksek risk anomali
                      <br />
                      <Text type="secondary">Kullanıcı-7832: Hızlı konum değişimi</Text>
                    </Timeline.Item>
                    <Timeline.Item color="blue">
                      <Text strong>13:32</Text> - Orta seviye anomali
                      <br />
                      <Text type="secondary">Kullanıcı-4521: Olağandışı giriş</Text>
                    </Timeline.Item>
                    <Timeline.Item color="green">
                      <Text strong>13:18</Text> - Düşük risk anomali çözüldü
                      <br />
                      <Text type="secondary">Kullanıcı-9043: False positive</Text>
                    </Timeline.Item>
                  </Timeline>
                </Card>
              </Col>
            </Row>
          </div>
        )}

        {/* AI Model Management Tab */}
        {radarActiveTab === 'models' && (
          <div>
            <Title level={4} style={{ color: '#722ed1' }}>
              ⚙️ AI Model Yönetimi & Öğrenme Sistemi
            </Title>
            
            {/* Learning Analytics Overview */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
              <Col span={4}>
                <Card size="small" style={{ backgroundColor: '#f0f5ff' }}>
                  <Statistic
                    title="Öğrenme Etkinlikleri"
                    value={learningAnalytics.totalLearningEvents}
                    prefix={<ThunderboltOutlined style={{ color: '#722ed1' }} />}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Card>
              </Col>
              <Col span={4}>
                <Card size="small" style={{ backgroundColor: '#f6ffed' }}>
                  <Statistic
                    title="Kullanıcı Feedback"
                    value={learningAnalytics.userFeedbacks}
                    prefix={<UserOutlined style={{ color: '#52c41a' }} />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col span={4}>
                <Card size="small" style={{ backgroundColor: '#fff2f0' }}>
                  <Statistic
                    title="Oto-Pattern Keşfi"
                    value={learningAnalytics.autoDiscoveredPatterns}
                    prefix={<EyeOutlined style={{ color: '#ff4d4f' }} />}
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Card>
              </Col>
              <Col span={4}>
                <Card size="small" style={{ backgroundColor: '#fff7e6' }}>
                  <Statistic
                    title="Haftalık Update"
                    value={learningAnalytics.modelUpdatesThisWeek}
                    prefix={<ReloadOutlined style={{ color: '#faad14' }} />}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Card>
              </Col>
              <Col span={4}>
                <Card size="small" style={{ backgroundColor: '#f0f5ff' }}>
                  <Statistic
                    title="Ort. Doğruluk Artışı"
                    value={learningAnalytics.avgAccuracyImprovement}
                    suffix="%"
                    prefix={<StarOutlined style={{ color: '#1890ff' }} />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col span={4}>
                <Card size="small" style={{ backgroundColor: '#f6ffed' }}>
                  <Statistic
                    title="Öğrenme Verimliliği"
                    value={learningAnalytics.learningEfficiency}
                    suffix="%"
                    prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* AI Models Management Table */}
            <Card 
              title={
                <Space>
                  <SettingOutlined style={{ color: '#722ed1' }} />
                  <span>AI Model Fleet Management</span>
                  <Badge count={aiModels.filter(m => m.autoLearning).length} />
                </Space>
              }
              extra={
                <Space>
                  <Button 
                    type="primary"
                    icon={<ThunderboltOutlined />}
                    loading={trainingInProgress}
                    onClick={() => {
                      setTrainingInProgress(true);
                      setTimeout(() => {
                        setTrainingInProgress(false);
                        message.success('🧠 Tüm modeller için toplu öğrenme tamamlandı - Ortalama +1.8% accuracy');
                      }, 5000);
                    }}
                  >
                    Toplu AI Training
                  </Button>
                  <Button icon={<ReloadOutlined />}>
                    Model Sync
                  </Button>
                </Space>
              }
              style={{ marginBottom: '24px' }}
            >
              <Table
                columns={[
                  {
                    title: 'AI Model',
                    key: 'model',
                    render: (_, record) => (
                      <Space direction="vertical" size={0}>
                        <Text strong style={{ color: '#722ed1' }}>{record.name} {record.version}</Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {record.type.replace('_', ' ').toUpperCase()}
                        </Text>
                      </Space>
                    ),
                  },
                  {
                    title: 'Durum',
                    dataIndex: 'status',
                    key: 'status',
                    width: 120,
                    render: (status) => {
                      const statusConfig = {
                        'active': { color: 'green', text: 'Aktif', icon: <CheckCircleOutlined /> },
                        'training': { color: 'orange', text: 'Eğitiliyor', icon: <ReloadOutlined spin /> },
                        'idle': { color: 'default', text: 'Beklemede', icon: <ClockCircleOutlined /> }
                      };
                      const config = statusConfig[status as keyof typeof statusConfig];
                      return (
                        <Tag color={config.color} icon={config.icon}>
                          {config.text}
                        </Tag>
                      );
                    },
                  },
                  {
                    title: 'Doğruluk',
                    dataIndex: 'accuracy',
                    key: 'accuracy',
                    width: 120,
                    render: (accuracy) => (
                      <Space>
                        <Progress 
                          percent={accuracy} 
                          size="small" 
                          status={accuracy > 95 ? 'success' : accuracy > 90 ? 'active' : 'exception'}
                          format={() => `${accuracy}%`}
                        />
                      </Space>
                    ),
                  },
                  {
                    title: 'Training Data',
                    dataIndex: 'trainingData',
                    key: 'trainingData',
                    width: 120,
                    render: (data) => (
                      <Text>{(data / 1000).toFixed(0)}K events</Text>
                    ),
                  },
                  {
                    title: 'Auto-Learning',
                    dataIndex: 'autoLearning',
                    key: 'autoLearning',
                    width: 120,
                    render: (autoLearning, record) => (
                      <Space>
                        <Switch 
                          checked={autoLearning}
                          size="small"
                          onChange={(checked) => {
                            message.success(`${record.name} auto-learning ${checked ? 'açıldı' : 'kapatıldı'}`);
                          }}
                        />
                        <Text style={{ fontSize: '12px' }}>
                          {autoLearning ? '🧠 Aktif' : '⏸️ Durduruldu'}
                        </Text>
                      </Space>
                    ),
                  },
                  {
                    title: 'Haftalık Gelişim',
                    dataIndex: 'improvements',
                    key: 'improvements',
                    width: 150,
                    render: (improvements) => (
                      <Text style={{ color: '#52c41a', fontSize: '12px' }}>
                        {improvements}
                      </Text>
                    ),
                  },
                  {
                    title: 'İşlemler',
                    key: 'actions',
                    width: 200,
                    render: (_, record) => (
                      <Space>
                        <Button 
                          size="small" 
                          icon={<EyeOutlined />}
                          onClick={() => {
                            Modal.info({
                              title: `🤖 ${record.name} Model Detayları`,
                              width: 600,
                              content: (
                                <Space direction="vertical" style={{ width: '100%' }}>
                                  <Text><strong>Versiyon:</strong> {record.version}</Text>
                                  <Text><strong>Son Eğitim:</strong> {new Date(record.lastTrained).toLocaleString('tr-TR')}</Text>
                                  <Text><strong>Learning Rate:</strong> {record.learningRate}</Text>
                                  <Text><strong>Epochs:</strong> {record.epochs}</Text>
                                  <Text><strong>False Positive Rate:</strong> {record.falsePositiveRate}%</Text>
                                  <Text><strong>Training Data:</strong> {record.trainingData.toLocaleString()} events</Text>
                                  <Text><strong>Auto-Learning:</strong> {record.autoLearning ? '✅ Aktif' : '❌ Devre Dışı'}</Text>
                                </Space>
                              ),
                            });
                          }}
                        >
                          Detaylar
                        </Button>
                        <Button 
                          size="small" 
                          type="primary"
                          icon={<ThunderboltOutlined />}
                          loading={selectedModelForTraining === record.id}
                          onClick={() => {
                            setSelectedModelForTraining(record.id);
                            setTimeout(() => {
                              setSelectedModelForTraining(null);
                              message.success(`🧠 ${record.name} için manuel training tamamlandı +${(Math.random() * 3 + 0.5).toFixed(1)}% accuracy`);
                            }, 4000);
                          }}
                        >
                          Manuel Training
                        </Button>
                        <Button 
                          size="small" 
                          icon={<BarChartOutlined />}
                          onClick={() => {
                            message.info(`📊 ${record.name} training metrics açılıyor...`);
                          }}
                        >
                          Metrics
                        </Button>
                      </Space>
                    ),
                  },
                ]}
                dataSource={aiModels}
                rowKey="id"
                size="small"
                pagination={false}
                rowClassName={(record) => 
                  record.status === 'training' ? 'warning-row' : 
                  record.accuracy > 95 ? 'success-row' : ''
                }
              />
            </Card>

            {/* Learning Progress & Feedback */}
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card 
                  title="🔄 Real-Time Learning Progress"
                  size="small"
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Text>Behavior Pattern Learning</Text>
                      <Progress percent={78} status="active" />
                    </div>
                    <div>
                      <Text>Threat Recognition Training</Text>
                      <Progress percent={92} status="success" />
                    </div>
                    <div>
                      <Text>False Positive Reduction</Text>
                      <Progress percent={65} status="active" />
                    </div>
                    <div>
                      <Text>User Feedback Integration</Text>
                      <Progress percent={88} status="success" />
                    </div>
                  </Space>
                </Card>
              </Col>
              <Col span={12}>
                <Card 
                  title="📊 Learning Analytics Dashboard"
                  size="small"
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Row justify="space-between">
                      <Text>Son 24 saat öğrenme:</Text>
                      <Text strong style={{ color: '#52c41a' }}>+127 events</Text>
                    </Row>
                    <Row justify="space-between">
                      <Text>Model sync başarısı:</Text>
                      <Text strong style={{ color: '#52c41a' }}>99.2%</Text>
                    </Row>
                    <Row justify="space-between">
                      <Text>Adaptive threshold updates:</Text>
                      <Text strong style={{ color: '#1890ff' }}>34 otomatik</Text>
                    </Row>
                    <Row justify="space-between">
                      <Text>False positive düşüşü:</Text>
                      <Text strong style={{ color: '#52c41a' }}>-12.4% bu hafta</Text>
                    </Row>
                    <Button 
                      type="primary" 
                      block 
                      icon={<BarChartOutlined />}
                      onClick={() => {
                        message.info('📊 Detaylı learning analytics açılıyor...');
                      }}
                    >
                      Detaylı Analytics
                    </Button>
                  </Space>
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </Card>
    </div>
  );

  // Support Page
  const renderSupportPage = () => (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Button 
              icon={<AlertOutlined />} 
              onClick={() => navigate('/emergency')}
              style={{ marginBottom: '16px' }}
            >
              ← Ana Emergency Dashboard
            </Button>
            <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
              📞 Kullanıcı Destek Hattı
            </Title>
            <Text type="secondary">
              GetMyHive kullanıcı desteği, ticket sistemi ve live chat
            </Text>
          </Col>
        </Row>
      </div>
      
      <Card title="📞 Destek Merkezi" style={{ minHeight: '60vh' }}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Text type="secondary" style={{ fontSize: '16px' }}>
            📞 Kullanıcı Destek Sistemi geliştiriliyor...
          </Text>
          <br />
          <Text type="secondary">
            Ticket sistemi, live chat, kullanıcı rehberliği ve problem çözümleri
          </Text>
        </div>
      </Card>
    </div>
  );

  // Technical Support Page
  const renderTechnicalPage = () => (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Button 
              icon={<AlertOutlined />} 
              onClick={() => navigate('/emergency')}
              style={{ marginBottom: '16px' }}
            >
              ← Ana Emergency Dashboard
            </Button>
            <Title level={2} style={{ margin: 0, color: '#faad14' }}>
              🔧 Teknik Destek
            </Title>
            <Text type="secondary">
              Sistem bakımı, diagnostics ve altyapı yönetimi
            </Text>
          </Col>
        </Row>
      </div>
      
      <Card title="🔧 Teknik Destek Merkezi" style={{ minHeight: '60vh' }}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Text type="secondary" style={{ fontSize: '16px' }}>
            🔧 Teknik Destek Sistemi geliştiriliyor...
          </Text>
          <br />
          <Text type="secondary">
            Sistem diagnostics, altyapı monitoring, bakım planlaması ve performance analizi
          </Text>
        </div>
      </Card>
    </div>
  );

  // BeeNetwork AI Proximity Analysis
  const [beeNetworkMembers] = useState(() => [
    {
      id: 'BEE-MEM-001',
      name: 'AYŞE KAYA',
      location: 'Şişli Mah. Metropol Residence B45',
      coordinates: { lat: 41.0370, lng: 28.9850 },
      status: 'online',
      deviceCount: 3,
      expertise: ['güvenlik', 'ilk yardım'],
      responseTime: '2-4dk',
      rating: 4.8,
      totalResponses: 127
    },
    {
      id: 'BEE-MEM-002', 
      name: 'MERVE ÖZTÜRK',
      location: 'Levent Mah. Villa Sitesi Villa 15',
      coordinates: { lat: 41.0450, lng: 28.9900 },
      status: 'online',
      deviceCount: 5,
      expertise: ['teknik destek', 'güvenlik'],
      responseTime: '1-3dk',
      rating: 4.9,
      totalResponses: 89
    },
    {
      id: 'BEE-MEM-003',
      name: 'ZEHRA YILMAZ', 
      location: 'Kadıköy Mah. Smart Towers C28',
      coordinates: { lat: 40.9900, lng: 29.0300 },
      status: 'busy',
      deviceCount: 2,
      expertise: ['ilk yardım', 'sosyal destek'],
      responseTime: '3-6dk',
      rating: 4.7,
      totalResponses: 156
    },
    {
      id: 'BEE-MEM-004',
      name: 'AYLIN DEMIR',
      location: 'Üsküdar Mah. Güvenlik Evleri D8', 
      coordinates: { lat: 41.0200, lng: 29.0200 },
      status: 'online',
      deviceCount: 4,
      expertise: ['güvenlik', 'teknik destek', 'koordinasyon'],
      responseTime: '1-2dk',
      rating: 5.0,
      totalResponses: 203
    },
    {
      id: 'BEE-MEM-005',
      name: 'FATMA ÖZKAN',
      location: 'Bahçelievler Mah. A Blok Daire 12',
      coordinates: { lat: 41.0000, lng: 28.9600 },
      status: 'online', 
      deviceCount: 3,
      expertise: ['sosyal destek', 'çocuk güvenliği'],
      responseTime: '2-5dk',
      rating: 4.6,
      totalResponses: 78
    }
  ]);

  // AI Proximity Analysis Function
  const calculateProximityAnalysis = (alarmLat: number, alarmLng: number) => {
    const getDistance = (lat1: number, lng1: number, lat2: number, lng2: number) => {
      const R = 6371; // Earth's radius in km
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLng = (lng2 - lng1) * Math.PI / 180;
      const a = Math.sin(dLat/2) * Math.sin(dLat/2) + 
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
                Math.sin(dLng/2) * Math.sin(dLng/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      return R * c; // Distance in km
    };

    return beeNetworkMembers
      .map(member => ({
        ...member,
        distance: getDistance(alarmLat, alarmLng, member.coordinates.lat, member.coordinates.lng),
        estimatedArrival: Math.ceil(getDistance(alarmLat, alarmLng, member.coordinates.lat, member.coordinates.lng) * 3) + 'dk'
      }))
      .filter(member => member.status === 'online' && member.distance < 5) // 5km radius
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 3); // Top 3 closest members
  };

  // Mock Map Data (5000+ alarms simulation) - LAND ONLY, NO SEA ALARMS
  const [mapAlarms] = useState<AlarmMarker[]>(() => {
    const districts = ['Bahçelievler', 'Şişli', 'Beşiktaş', 'Kadıköy', 'Üsküdar', 'Fatih', 'Beyoğlu', 'Maltepe'];
    const alarmTypes = ['panic_button', 'home_security', 'motion_sensor', 'door_sensor', 'device_offline'];
    const severities: ('critical' | 'high' | 'medium' | 'low')[] = ['critical', 'high', 'medium', 'low'];
    const users = ['RASİM ÇETİN', 'AYŞE KAYA', 'MERVE ÖZTÜRK', 'ZEHRA YILMAZ', 'AYLIN DEMIR', 'FATMA ÖZKAN', 'EMINE YILDIZ'];
    
    // Istanbul land-only coordinate zones
    const landZones = [
      { lat: 41.0082, lng: 28.9784, radius: 0.06 }, // Fatih/Eminönü area
      { lat: 41.0370, lng: 28.9850, radius: 0.04 }, // Şişli/Beyoğlu area
      { lat: 41.0000, lng: 28.9600, radius: 0.05 }, // Bahçelievler area
      { lat: 41.0200, lng: 29.0200, radius: 0.04 }, // Üsküdar area
      { lat: 40.9900, lng: 29.0300, radius: 0.05 }, // Kadıköy area
      { lat: 41.0100, lng: 28.9400, radius: 0.04 }, // Zeytinburnu area
    ];
    
    return Array.from({ length: 5247 }, (_, i) => {
      // Randomly pick a land zone
      const zone = landZones[Math.floor(Math.random() * landZones.length)];
      
      // Generate coordinates within the land zone
      const angle = Math.random() * 2 * Math.PI;
      const radius = Math.random() * zone.radius;
      const lat = zone.lat + radius * Math.cos(angle);
      const lng = zone.lng + radius * Math.sin(angle);
      
      return {
        id: `LAND-ALARM-${i + 1}`,
        lat,
        lng,
        type: alarmTypes[Math.floor(Math.random() * alarmTypes.length)],
        severity: severities[Math.floor(Math.random() * severities.length)],
        user: users[Math.floor(Math.random() * users.length)],
        timestamp: new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000).toISOString(),
        status: Math.random() > 0.7 ? 'resolved' : 'active'
      };
    });
  });

  const [mapFilter, setMapFilter] = useState('all');
  const [mapClusterLevel, setMapClusterLevel] = useState(100);

  // High-Performance Map Page
  const renderMapPage = () => {
    const activeAlarms = mapAlarms.filter(alarm => alarm.status === 'active');
    const criticalAlarms = activeAlarms.filter(alarm => alarm.severity === 'critical');
    const todayAlarms = mapAlarms.filter(alarm => 
      new Date(alarm.timestamp).toDateString() === new Date().toDateString()
    );

    return (
      <div>
        <div style={{ marginBottom: '24px' }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Button 
                icon={<AlertOutlined />} 
                onClick={() => navigate('/emergency')}
                style={{ marginBottom: '16px' }}
              >
                ← Ana Emergency Dashboard
              </Button>
              <Title level={2} style={{ margin: 0, color: '#ff4d4f' }}>
                🗺️ Emergency Harita Sistemi
              </Title>
              <Text type="secondary">
                {mapAlarms.length.toLocaleString()} alarm kapasiteli yüksek performanslı harita
              </Text>
            </Col>
            <Col>
              <Space>
                <Select
                  value={mapFilter}
                  onChange={setMapFilter}
                  style={{ width: 150 }}
                >
                  <Option value="all">Tüm Alarmlar ({mapAlarms.length})</Option>
                  <Option value="active">Aktif ({activeAlarms.length})</Option>
                  <Option value="critical">Kritik ({criticalAlarms.length})</Option>
                  <Option value="today">Bugün ({todayAlarms.length})</Option>
                </Select>
                <Button icon={<ReloadOutlined />}>
                  Yenile
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        {/* Map Statistics */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Toplam Alarm"
                value={mapAlarms.length}
                prefix={<EnvironmentOutlined style={{ color: '#13c2c2' }} />}
                valueStyle={{ color: '#13c2c2' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Aktif Alarmlar"
                value={activeAlarms.length}
                prefix={<AlertOutlined style={{ color: '#ff4d4f' }} />}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Kritik Durumlar"
                value={criticalAlarms.length}
                prefix={<FireOutlined style={{ color: '#ff4d4f' }} />}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Bugün"
                value={todayAlarms.length}
                prefix={<ClockCircleOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Map Container */}
        <Card 
          title={
            <Space>
              <EnvironmentOutlined style={{ color: '#13c2c2' }} />
              <span>Gerçek Zamanlı Emergency Haritası</span>
              <Tag color="blue">Performance Mode</Tag>
            </Space>
          }
          extra={
            <Space>
              <Text>Cluster Seviyesi:</Text>
              <Slider 
                min={50} 
                max={500} 
                value={mapClusterLevel}
                onChange={setMapClusterLevel}
                style={{ width: 100 }}
              />
              <Text>{mapClusterLevel}</Text>
            </Space>
          }
          style={{ minHeight: '70vh' }}
        >
          {/* Real Google Maps Integration */}
          <GoogleMapComponent
            alarms={mapAlarms.filter(alarm => {
              if (mapFilter === 'critical') return alarm.severity === 'critical';
              if (mapFilter === 'active') return alarm.status === 'active';
              if (mapFilter === 'today') return new Date(alarm.timestamp).toDateString() === new Date().toDateString();
              return alarm.severity === 'critical' || alarm.severity === 'high';
            })}
            center={{ lat: 41.0082, lng: 28.9784 }} // Istanbul center
            zoom={11}
            clusterLevel={mapClusterLevel}
            onMarkerClick={(alarm) => {
              const proximityData = calculateProximityAnalysis(alarm.lat, alarm.lng);
              message.success(`🚨 ${alarm.type} - ${alarm.user} (${alarm.severity})`);
              
              // Show AI proximity analysis
              if (proximityData.length > 0) {
                const nearestMember = proximityData[0];
                message.info(
                  `🤖 AI: En yakın BeeNetwork üyesi ${nearestMember.name} (${nearestMember.distance.toFixed(1)}km - ${nearestMember.estimatedArrival})`
                );
              } else {
                message.warning('🤖 AI: 5km çevrede müsait BeeNetwork üyesi bulunamadı');
              }
            }}
            onAddressResolved={(alarm, address) => {
              message.info(`📍 Adres: ${address}`);
            }}
            onStreetViewOpen={(alarm) => {
              message.info(`👁️ Street View açılıyor: ${alarm.user}`);
            }}
            onSendMessage={(alarm) => {
              setSelectedMessage({
                id: `MAP-MSG-${alarm.id}`,
                type: 'emergency',
                from: `Harita Operatörü`,
                deviceId: alarm.id,
                message: `${alarm.user} için harita üzerinden mesaj gönderildi`,
                timestamp: new Date().toISOString(),
                status: 'new',
                priority: alarm.severity === 'critical' ? 'critical' : 'high',
                location: alarm.user
              });
              setChatDrawerVisible(true);
              message.success(`💬 ${alarm.user} için mesaj gönderildi`);
            }}
          />

          
          {/* Map overlay controls */}
          <div style={{ 
            position: 'absolute',
            bottom: '20px',
            left: '20px',
            backgroundColor: 'rgba(255,255,255,0.95)',
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid #d9d9d9'
          }}>
            <Space direction="vertical" size="small">
              <Text strong style={{ fontSize: '12px' }}>🏢 Kara Alarmları</Text>
              <Text style={{ fontSize: '11px' }}>
                {mapFilter === 'all' ? 'Kritik + Yüksek (Deniz Hariç)' : mapFilter.toUpperCase()}
              </Text>
              <Text style={{ fontSize: '11px' }}>
                Gösterilen: {mapAlarms.filter(alarm => {
                  if (mapFilter === 'critical') return alarm.severity === 'critical';
                  if (mapFilter === 'active') return alarm.status === 'active';
                  if (mapFilter === 'today') return new Date(alarm.timestamp).toDateString() === new Date().toDateString();
                  return alarm.severity === 'critical' || alarm.severity === 'high';
                }).length} / {mapAlarms.length}
              </Text>
              <Text style={{ fontSize: '10px', color: '#52c41a' }}>
                ✅ Sadece şehir içi alarmlar
              </Text>
            </Space>
          </div>

          {/* Performance Analytics */}
          <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
            <Col span={12}>
              <Card size="small">
                <Statistic
                  title="Harita Performansı"
                  value={99.8}
                  suffix="%"
                  prefix={<DashboardOutlined style={{ color: '#52c41a' }} />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card size="small">
                <Statistic
                  title="Cluster Efficiency"
                  value={Math.floor(mapAlarms.length / mapClusterLevel)}
                  suffix="clusters"
                  prefix={<EnvironmentOutlined style={{ color: '#13c2c2' }} />}
                  valueStyle={{ color: '#13c2c2' }}
                />
              </Card>
            </Col>
          </Row>
        </Card>

        {/* Animation for critical alarms */}
        <style>{`
          @keyframes pulse {
            0% { transform: translate(-50%, -50%) scale(1); }
            50% { transform: translate(-50%, -50%) scale(1.1); }
            100% { transform: translate(-50%, -50%) scale(1); }
          }
        `}</style>
      </div>
    );
  };

  return (
    <Content style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      {renderPageContent()}

      {/* Alert Detail Drawer */}
      <Drawer
        title={
          <Space>
            {selectedAlert && getAlertIcon(selectedAlert.type)}
            <span>Acil Durum Detayları</span>
            {selectedAlert && (
              <Tag color={getSeverityColor(selectedAlert.severity)}>
                {selectedAlert.severity.toUpperCase()}
              </Tag>
            )}
          </Space>
        }
        placement="right"
        width={600}
        onClose={() => setAlertDrawerVisible(false)}
        open={alertDrawerVisible}
        extra={
          <Space>
            <Button type="primary" icon={<PhoneOutlined />}>
              İhbarcıyı Ara
            </Button>
            <Button icon={<EnvironmentOutlined />}>
              Konumu Göster
            </Button>
          </Space>
        }
      >
        {selectedAlert && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Alert
              message={selectedAlert.title || 'Acil Durum Detayı'}
              description={selectedAlert.description || 'Detay bilgisi mevcut değil'}
              type={selectedAlert.severity === 'critical' ? 'error' : 'warning'}
              showIcon
            />

            <Descriptions title="Olay Bilgileri" bordered column={1}>
              <Descriptions.Item label="Olay ID">
                {selectedAlert.id}
              </Descriptions.Item>
              <Descriptions.Item label="Tür">
                <Space>
                  {getAlertIcon(selectedAlert.type)}
                  {selectedAlert.type}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Önem Derecesi">
                <Tag color={getSeverityColor(selectedAlert.severity)}>
                  {selectedAlert.severity.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Durum">
                <Tag color={getStatusColor(selectedAlert.status)}>
                  {getStatusText(selectedAlert.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Olay Zamanı">
                {new Date(selectedAlert.timestamp).toLocaleString('tr-TR')}
              </Descriptions.Item>
              <Descriptions.Item label="Atanan Ekip">
                {selectedAlert.assignedTeam || 'Henüz atanmadı'}
              </Descriptions.Item>
              {selectedAlert.estimatedResponse && (
                <Descriptions.Item label="Tahmini Varış">
                  {selectedAlert.estimatedResponse} dakika
                </Descriptions.Item>
              )}
            </Descriptions>

            <Descriptions title="Konum Bilgileri" bordered column={1}>
              <Descriptions.Item label="Adres">
                {selectedAlert.location?.address || 'Adres bilgisi mevcut değil'}
              </Descriptions.Item>
              <Descriptions.Item label="İlçe">
                {selectedAlert.location?.district || 'İlçe bilgisi mevcut değil'}
              </Descriptions.Item>
              <Descriptions.Item label="Koordinatlar">
                {selectedAlert.location?.coordinates?.[0] || 'N/A'}, {selectedAlert.location?.coordinates?.[1] || 'N/A'}
              </Descriptions.Item>
            </Descriptions>

            <Descriptions title="Kullanıcı Bilgileri" bordered column={1}>
              <Descriptions.Item label="Ad Soyad">
                {selectedAlert.user?.name || selectedAlert.reporter?.name || 'Kullanıcı bilgisi mevcut değil'}
              </Descriptions.Item>
              <Descriptions.Item label="Telefon">
                <Space>
                  {selectedAlert.user?.phone || selectedAlert.reporter?.phone || 'Telefon bilgisi mevcut değil'}
                  <Button 
                    size="small" 
                    type="link" 
                    icon={<PhoneOutlined />}
                    onClick={() => {
                      const phone = selectedAlert.user?.phone || selectedAlert.reporter?.phone;
                      if (phone) {
                        message.info(`Aranıyor: ${phone}`);
                      } else {
                        message.warning('Telefon bilgisi bulunamadı');
                      }
                    }}
                  />
                </Space>
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <Space direction="vertical" style={{ width: '100%' }}>
              <Title level={5}>Hızlı İşlemler</Title>
              <Space wrap>
                <Button type="primary" icon={<TeamOutlined />}>
                  Ekip Ata
                </Button>
                <Button icon={<CarOutlined />}>
                  Sevkiyat
                </Button>
                <Button icon={<PhoneOutlined />}>
                  İhbarcıyı Ara
                </Button>
                <Button icon={<EnvironmentOutlined />}>
                  Navigasyon
                </Button>
                <Button danger icon={<CheckCircleOutlined />}>
                  Durumu Kapat
                </Button>
              </Space>
            </Space>
          </Space>
        )}
      </Drawer>

      {/* Custom Styles */}
      <style>{`
        .critical-row {
          background-color: #fff2f0 !important;
          border-left: 4px solid #ff4d4f;
        }
        .critical-row:hover {
          background-color: #fff1f0 !important;
        }
        .ant-table-tbody > tr.critical-row > td {
          border-bottom: 1px solid #ffccc7;
        }
      `}</style>
    </Content>
  );
};

export default EmergencyPage;